import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AdminApi } from '../../services/api';
import {
  Users, BookOpen, GraduationCap, TrendingUp, Activity,
  BarChart3, Settings, LogOut, RefreshCw, Search, Filter,
  ChevronDown, Calendar, AlertCircle, CheckCircle, Clock,
  UserPlus, BookPlus, Shield, Eye, Edit, Trash2, Ban,
  MessageSquare, DollarSign, Layers, Bot, Zap, Brain,
  Sparkles, Play, Send, Terminal, Cpu, Radio, Target,
  Bell, Network, AlertTriangle, Power, UserCog, FileText,
  TrendingDown, Gauge, Crown, Check, X, Info, Save,
  ArrowLeft, Image, Tag, Star, Award, Heart, UserCheck,
  XCircle, Mail, Briefcase, Link, ExternalLink, Inbox, Reply, Archive,
  Monitor, MessageCircle, Mic, Video, Download, CreditCard, Wallet,
  Receipt, PieChart, LineChart, BarChart2, Calendar as CalendarIcon,
  FlaskConical, Award as AwardIcon
} from 'lucide-react';
import './AdminDashboard.css';
import './AdminDashboardShell.css';
import AdminDashboardShell from './AdminDashboardShell';
import AdminSettingsPanel from './AdminSettingsPanel';
import AdminAnalyticsPanel from './AdminAnalyticsPanel';
import AdminOverviewPanel from './AdminOverviewPanel';
import {
  QoreTalksEpisodesPanel,
  QoreTalksSubscribersPanel,
  QoreTalksLivestreamPanel,
  QoreTalksGuestsPanel,
} from './QoreTalksAdminPanels';
import {
  QoreConnectCommunityPanel,
  QoreConnectForumPanel,
  QoreConnectJobsPanel,
} from './QoreConnectAdminPanels';
import {
  AcademyLabsPanel,
  AcademyCertificatesPanel,
  AcademyCareerPanel,
  AcademyTutorPanel,
} from './QoreAcademyOpsPanels';
import {
  SupportTicketsPanel,
  EmailTemplatesPanel,
  FinancePanel,
} from './AdminSupportPanels';
import AdminCopilotDock from './AdminCopilotDock';
// ============================================
// NOTIFICATION COMPONENT
// ============================================
const Notification = ({ notifications, onDismiss }) => {
  if (!notifications.length) return null;
  
  return (
    <div className="notification-container">
      {notifications.map(notif => (
        <div key={notif.id} className={`notification ${notif.type}`}>
          <div className="notification-icon">
            {notif.type === 'success' && <CheckCircle size={20} />}
            {notif.type === 'error' && <AlertCircle size={20} />}
            {notif.type === 'warning' && <AlertTriangle size={20} />}
            {notif.type === 'info' && <Info size={20} />}
          </div>
          <div className="notification-content">
            <h4>{notif.title}</h4>
            <p>{notif.message}</p>
          </div>
          <button className="notification-close" onClick={() => onDismiss(notif.id)}>
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
};

// ============================================
// CONFIRMATION MODAL COMPONENT
// ============================================
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirm', danger = false }) => {
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="confirm-icon">
          {danger ? <AlertTriangle size={48} /> : <AlertCircle size={48} />}
        </div>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="confirm-actions">
          <button className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button className={`btn-confirm ${danger ? 'danger' : ''}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COURSE VIEW MODAL
// ============================================
const CourseViewModal = ({ course, isOpen, onClose }) => {
  if (!isOpen || !course) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="course-modal view-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{course.title}</h2>
          <button className="btn-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="course-view-grid">
            <div className="course-view-main">
              {course.thumbnail && (
                <div className="course-thumbnail">
                  <img src={course.thumbnail} alt={course.title} />
                </div>
              )}
              <div className="course-section">
                <h4>Description</h4>
                <p>{course.description || 'No description available'}</p>
              </div>
              <div className="course-section">
                <h4>Lessons ({course.lessons?.length || 0})</h4>
                <div className="lessons-list">
                  {course.lessons?.length > 0 ? (
                    course.lessons.map((lesson, i) => (
                      <div key={lesson._id || i} className="lesson-item">
                        <span className="lesson-number">{i + 1}</span>
                        <span className="lesson-title">{lesson.title}</span>
                        <span className="lesson-duration">{lesson.duration || '—'}</span>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No lessons added yet</p>
                  )}
                </div>
              </div>
            </div>
            <div className="course-view-sidebar">
              <div className="course-info-card">
                <h4>Course Info</h4>
                <div className="info-row">
                  <span>Status</span>
                  <span className={`status ${course.status || (course.isPublished ? 'published' : 'draft')}`}>
                    {course.status || (course.isPublished ? 'Published' : 'Draft')}
                  </span>
                </div>
                <div className="info-row">
                  <span>Category</span>
                  <span>{course.category || 'Uncategorized'}</span>
                </div>
                <div className="info-row">
                  <span>Level</span>
                  <span>{course.level || 'All Levels'}</span>
                </div>
                <div className="info-row">
                  <span>Price</span>
                  <span>{course.price > 0 ? `$${course.price}` : 'Free'}</span>
                </div>
                <div className="info-row">
                  <span>Enrollments</span>
                  <span>{course.analytics?.enrollmentCount || course.enrollmentCount || 0}</span>
                </div>
                <div className="info-row">
                  <span>Rating</span>
                  <span>
                    <Star size={14} /> {typeof course.rating === 'object' 
                      ? (course.rating?.average?.toFixed(1) || 'N/A') 
                      : (course.rating || 'N/A')}
                    {typeof course.rating === 'object' && course.rating?.count > 0 && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginLeft: '4px' }}>
                        ({course.rating.count})
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="course-info-card">
                <h4>Instructor</h4>
                <p>{course.instructor?.name || course.instructor?.id || 'Unknown'}</p>
              </div>
              <div className="course-info-card">
                <h4>Created</h4>
                <p>{course.createdAt ? new Date(course.createdAt).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COURSE EDIT MODAL
// ============================================
const CourseEditModal = ({ course, isOpen, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    level: 'Beginner',
    price: 0,
    status: 'draft',
    thumbnail: ''
  });
  
  useEffect(() => {
    if (course) {
      setFormData({
        title: course.title || '',
        description: course.description || '',
        category: course.category || '',
        level: course.level || 'Beginner',
        price: course.price || 0,
        status: course.status || (course.isPublished ? 'published' : 'draft'),
        thumbnail: course.thumbnail || ''
      });
    }
  }, [course]);
  
  if (!isOpen) return null;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(course._id, formData);
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="course-modal edit-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Edit size={20} /> Edit Course</h2>
          <button className="btn-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Level</label>
                <select
                  value={formData.level}
                  onChange={e => setFormData(f => ({ ...f, level: e.target.value }))}
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Price ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={e => setFormData(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData(f => ({ ...f, status: e.target.value }))}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="pending-approval">Pending Approval</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Thumbnail URL</label>
              <input
                type="url"
                value={formData.thumbnail}
                onChange={e => setFormData(f => ({ ...f, thumbnail: e.target.value }))}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? <RefreshCw size={16} className="spin" /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// COURSE APPROVAL MODAL
// ============================================
const CourseApprovalModal = ({ course, isOpen, onClose, onApprove, onReject }) => {
  const [rejecting, setRejecting] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) { setRejecting(false); setReason(''); }
  }, [isOpen]);

  if (!isOpen || !course) return null;

  const handleApprove = async () => {
    setSubmitting(true); await onApprove(course._id); setSubmitting(false); onClose();
  };
  const handleReject = async () => {
    if (!reason.trim()) return;
    setSubmitting(true); await onReject(course._id, reason); setSubmitting(false); onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="course-approval-modal" onClick={e => e.stopPropagation()}>
        <div className="approval-banner">
          {course.thumbnail
            ? <img src={course.thumbnail} alt={course.title} className="approval-thumbnail" />
            : <div className="approval-thumbnail-placeholder"><BookOpen size={48} /><span>No Thumbnail</span></div>}
          <button className="btn-close approval-close" onClick={onClose}><X size={20} /></button>
          <div className="approval-status-badge"><Clock size={14} /> Pending Approval</div>
        </div>
        <div className="approval-body">
          <h2 className="approval-title">{course.title}</h2>
          <div className="approval-meta-row">
            {course.category && <span className="approval-tag"><Tag size={12} />{course.category}</span>}
            {course.level && <span className="approval-tag"><Award size={12} />{course.level}</span>}
            <span className="approval-tag"><DollarSign size={12} />{course.price > 0 ? `$${course.price}` : 'Free'}</span>
            <span className="approval-tag"><Layers size={12} />{course.lessons?.length || 0} lessons</span>
          </div>
          <div className="approval-section">
            <h4>Description</h4>
            <p className="approval-description">{course.description || 'No description provided.'}</p>
          </div>
          <div className="approval-section">
            <h4>Instructor</h4>
            <div className="approval-instructor">
              <div className="instructor-avatar">
                {(course.instructor?.name || course.instructor?.id || 'I').charAt(0).toUpperCase()}
              </div>
              <span>{course.instructor?.name || course.instructor?.id || 'Unknown Instructor'}</span>
            </div>
          </div>
          {course.lessons?.length > 0 && (
            <div className="approval-section">
              <h4>Lesson Outline ({course.lessons.length})</h4>
              <div className="approval-lessons">
                {course.lessons.slice(0, 6).map((lesson, i) => (
                  <div key={i} className="approval-lesson-item">
                    <span className="lesson-num">{i + 1}</span>
                    <span className="lesson-ttl">{lesson.title}</span>
                    {lesson.duration && <span className="lesson-dur">{lesson.duration}</span>}
                  </div>
                ))}
                {course.lessons.length > 6 && (
                  <p className="approval-more">+{course.lessons.length - 6} more lessons…</p>
                )}
              </div>
            </div>
          )}
          {!rejecting ? (
            <div className="approval-actions">
              <button className="btn-approve-course" onClick={handleApprove} disabled={submitting}>
                {submitting ? <RefreshCw size={16} className="spin" /> : <CheckCircle size={16} />} Approve Course
              </button>
              <button className="btn-reject-course" onClick={() => setRejecting(true)} disabled={submitting}>
                <XCircle size={16} /> Reject Course
              </button>
            </div>
          ) : (
            <div className="rejection-panel">
              <h4><AlertTriangle size={16} /> Rejection Reason</h4>
              <textarea
                className="rejection-textarea"
                placeholder="Provide a clear reason so the instructor can improve their course…"
                value={reason} onChange={e => setReason(e.target.value)} rows={4}
              />
              <div className="rejection-actions">
                <button className="btn-cancel" onClick={() => setRejecting(false)}>Back</button>
                <button className="btn-reject-course" onClick={handleReject} disabled={!reason.trim() || submitting}>
                  {submitting ? <RefreshCw size={16} className="spin" /> : <XCircle size={16} />} Submit Rejection
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSubTab, setActiveSubTab] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);

  
  // Pagination
  const [userPage, setUserPage] = useState(1);
  const [coursePage, setCoursePage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [courseTotal, setCourseTotal] = useState(0);
  
  // Filters
  const [userFilter, setUserFilter] = useState({ role: '', search: '' });
  const [courseFilter, setCourseFilter] = useState({ status: '', search: '' });

  // Financial Management State
  const [financialData, setFinancialData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [financialPeriod, setFinancialPeriod] = useState('30d');
  const [financialDateRange, setFinancialDateRange] = useState({ start: '', end: '' });
  const [transactionFilter, setTransactionFilter] = useState({ status: 'all', type: 'all' });
  const [financialLoading, setFinancialLoading] = useState(false);
  
  // AI Agent state
  const [agentStatus, setAgentStatus] = useState(null);
  const [agentHealth, setAgentHealth] = useState(null);
  const [agentScan, setAgentScan] = useState(null);
  const [agentInsights, setAgentInsights] = useState(null);
  const [agentChat, setAgentChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);
  const [scanRunning, setScanRunning] = useState(false);
  const chatEndRef = useRef(null);
  
  // NOVA v3.0 - Sub-Agents
  const [subAgents, setSubAgents] = useState([]);
  const [agentActivity, setAgentActivity] = useState([]);
  const [autonomousMode, setAutonomousMode] = useState(true);

  // Atlas Sub-Agent - User Engagement State
  const [atlasData, setAtlasData] = useState(null);
  const [atlasLoading, setAtlasLoading] = useState(false);
  const [atlasActionRunning, setAtlasActionRunning] = useState(null);
  const [atlasLogs, setAtlasLogs] = useState([]);
  const [atRiskUsers, setAtRiskUsers] = useState([]);

  // Mentorship Management State
  const [mentorshipStats, setMentorshipStats] = useState(null);
  const [mentors, setMentors] = useState([]);
  const [mentoringSessions, setMentoringSessions] = useState([]);
  const [mentorFilter, setMentorFilter] = useState({ status: '', search: '' });
  const [sessionFilter, setSessionFilter] = useState({ status: '' });
  const [mentorshipLoading, setMentorshipLoading] = useState(false);
  const [processingMentor, setProcessingMentor] = useState(null);

  // Contact Messages State
  const [contactMessages, setContactMessages] = useState([]);
  const [contactStats, setContactStats] = useState(null);
  const [contactFilter, setContactFilter] = useState({ status: '', inquiryType: '', search: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');

  // QoreTalks State
  const [guestApplications, setGuestApplications] = useState([]);
  const [applicationStats, setApplicationStats] = useState(null);
  const [applicationFilter, setApplicationFilter] = useState({ status: 'all' });
  const [qoretalksLoading, setQoretalksLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationNotes, setApplicationNotes] = useState('');

  // User Management State
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailModal, setUserDetailModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [addUserModal, setAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '', role: 'student', password: '' });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [userStats, setUserStats] = useState(null);
  // System Health State
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemHealthLoading, setSystemHealthLoading] = useState(false);
  const [systemHealthLastRefresh, setSystemHealthLastRefresh] = useState(null);
  // Course Approval Modal State
  const [approvalModal, setApprovalModal] = useState({ isOpen: false, course: null });

  // E-Learning Management State
  const [elearningStats, setElearningStats] = useState(null);
  const [generatedPaths, setGeneratedPaths] = useState([]);
  const [pathsLoading, setPathsLoading] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [pathDetailModal, setPathDetailModal] = useState(false);
  const [generationLimits, setGenerationLimits] = useState({ daily: 5, monthly: 50, unlimited: false });
  const [userGenerationStats, setUserGenerationStats] = useState([]);
  const [generationHistoryFilter, setGenerationHistoryFilter] = useState({ user: '', status: 'all', dateRange: '7d' });
  const [editLimitsModal, setEditLimitsModal] = useState(false);
  const [selectedUserForLimits, setSelectedUserForLimits] = useState(null);
  const [customLimits, setCustomLimits] = useState({ daily: 5, monthly: 50, unlimited: false });

  // Notification System
  const [notifications, setNotifications] = useState([]);
  
  // Modal States
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, danger: false });
  const [viewCourse, setViewCourse] = useState(null);
  const [editCourse, setEditCourse] = useState(null);
  const [savingCourse, setSavingCourse] = useState(false);
  const [commandInbox, setCommandInbox] = useState([]);
  const [crossLine, setCrossLine] = useState([]);
  const [toolBusy, setToolBusy] = useState(null);
  const [copilotOpen, setCopilotOpen] = useState(false);

  // Shell layout state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ============================================
  // NOTIFICATION HELPERS
  // ============================================
  const showNotification = (type, title, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, title, message }]);
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Auth check
  useEffect(() => {
    if (!isAuthenticated || (user && user.role !== 'admin')) {
      navigate('/login');
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    try {
      const response = await AdminApi.getDashboard(token);
      if (response.success) {
        setDashboard(response.data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data');
    }
  }, [token]);

  const fetchCommandInbox = useCallback(async () => {
    if (!token) return;
    try {
      const [inboxRes, xlRes] = await Promise.all([
        AdminApi.getCommandInbox(token),
        AdminApi.getCrossLine(token),
      ]);
      if (inboxRes.success) setCommandInbox(inboxRes.data || []);
      if (xlRes.success) setCrossLine(xlRes.data || []);
    } catch (err) {
      console.error('Command inbox error:', err);
    }
  }, [token]);

  const runCommandTool = async (name, args, label) => {
    if (!token) return;
    const key = `${name}:${JSON.stringify(args)}`;
    setToolBusy(key);
    try {
      const res = await AdminApi.executeCommandTool(token, name, args);
      const ok = res.success && res.data?.success !== false;
      showNotification(ok ? 'success' : 'error', label || name, res.data?.message || res.message || (ok ? 'Done' : 'Failed'));
      await Promise.all([fetchCommandInbox(), fetchDashboard()]);
    } catch (err) {
      showNotification('error', label || name, err.message);
    } finally {
      setToolBusy(null);
    }
  };

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const params = { page: userPage, limit: 10 };
      if (userFilter.role) params.role = userFilter.role;
      if (userFilter.search) params.search = userFilter.search;
      
      const response = await AdminApi.getUsers(token, params);
      if (response.success) {
        setUsers(response.data.users);
        setUserTotal(response.data.pagination.total);
      }
    } catch (err) {
      console.error('Users fetch error:', err);
    }
  }, [token, userPage, userFilter]);

  // Fetch courses
  const fetchCourses = useCallback(async () => {
    if (!token) return;
    try {
      const params = { page: coursePage, limit: 10 };
      if (courseFilter.status) params.status = courseFilter.status;
      if (courseFilter.search) params.search = courseFilter.search;
      
      const response = await AdminApi.getCourses(token, params);
      if (response.success) {
        setCourses(response.data.courses || response.data);
        setCourseTotal(response.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Courses fetch error:', err);
    }
  }, [token, coursePage, courseFilter]);

  // Fetch activity logs
  const fetchActivityLogs = useCallback(async () => {
    if (!token) return;
    try {
      const response = await AdminApi.getActivityLogs(token, { limit: 20 });
      if (response.success) {
        setActivityLogs(response.data.logs || response.data);
      }
    } catch (err) {
      console.error('Activity logs fetch error:', err);
    }
  }, [token]);



  // Fetch financial data
  const fetchFinancialData = useCallback(async () => {
    if (!token) return;
    setFinancialLoading(true);
    try {
      const params = { period: financialPeriod };
      if (financialDateRange.start && financialDateRange.end) {
        params.startDate = financialDateRange.start;
        params.endDate = financialDateRange.end;
      }
      const response = await AdminApi.getPaymentsDashboard(token, params);
      if (response.success) {
        setFinancialData(response.data);
        setTransactions(response.data.recentPayments || []);
      } else {
        setFinancialData(null);
        setTransactions([]);
      }
    } catch (err) {
      console.error('Financial data fetch error:', err);
    } finally {
      setFinancialLoading(false);
    }
  }, [token, financialPeriod, financialDateRange]);



  // Fetch System Health
  const fetchSystemHealth = useCallback(async () => {
    if (!token) return;
    setSystemHealthLoading(true);
    const fallback = {
      apiResponseTime: { value: 245, unit: 'ms', status: 'ok' },
      dbConnection: { status: 'ok', latency: 12 },
      memoryUsage: { used: 512, total: 1024, percentage: 50, status: 'ok' },
      uptime: { seconds: 864000, formatted: '10d 0h 0m', status: 'ok' },
      errorRate: { value: 0.4, status: 'ok' },
      activeConnections: { value: 38, status: 'ok' }
    };
    try {
      const response = await AdminApi.getSystemHealth(token);
      setSystemHealth(response.success ? response.data : fallback);
    } catch (_) {
      setSystemHealth(fallback);
    } finally {
      setSystemHealthLoading(false);
      setSystemHealthLastRefresh(new Date());
    }
  }, [token]);

  // Export selected users as CSV
  const exportSelectedUsers = useCallback(() => {
    const selected = users.filter(u => selectedUsers.includes(u._id));
    if (selected.length === 0) return;
    const header = ['ID','First Name','Last Name','Email','Role','Status','Joined','Last Login'];
    const rows = selected.map(u => [
      u._id, u.firstName||'', u.lastName||'', u.email||'',
      u.role||'', u.status||'active',
      u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
      u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'
    ]);
    const csv = [header,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href=url; a.download=`users-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showNotification('success','CSV Exported',`${selected.length} user(s) exported.`);
  }, [users, selectedUsers]);

  // Bulk suspend selected users
  const handleBulkSuspend = useCallback(async () => {
    if (selectedUsers.length === 0) return;
    setConfirmModal({
      isOpen: true, title: 'Suspend Selected Users',
      message: `Suspend ${selectedUsers.length} user(s)?`,
      danger: true, confirmText: 'Suspend All',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          await Promise.all(selectedUsers.map(id => AdminApi.updateUser(token, id, { status: 'banned' })));
          showNotification('success','Users Suspended',`${selectedUsers.length} user(s) suspended.`);
          setSelectedUsers([]); fetchUsers();
        } catch(err) { showNotification('error','Bulk Suspend Failed',err.message); }
      }
    });
  }, [selectedUsers, token, fetchUsers]);

  // Bulk activate selected users
  const handleBulkActivate = useCallback(async () => {
    if (selectedUsers.length === 0) return;
    setConfirmModal({
      isOpen: true, title: 'Activate Selected Users',
      message: `Activate ${selectedUsers.length} user(s)?`,
      danger: false, confirmText: 'Activate All',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          await Promise.all(selectedUsers.map(id => AdminApi.updateUser(token, id, { status: 'active' })));
          showNotification('success','Users Activated',`${selectedUsers.length} user(s) activated.`);
          setSelectedUsers([]); fetchUsers();
        } catch(err) { showNotification('error','Bulk Activate Failed',err.message); }
      }
    });
  }, [selectedUsers, token, fetchUsers]);

  // Course approval handlers
  const handleOpenApprovalModal = (course) => setApprovalModal({ isOpen: true, course });

  const handleApproveCourse = async (courseId) => {
    try {
      const r = await AdminApi.approveCourse(token, courseId);
      if (r.success) { showNotification('success','Course Approved','Now live.'); fetchCourses(); }
      else showNotification('error','Approval Failed', r.message || 'Failed.');
    } catch(err) { showNotification('error','Approval Failed',err.message); }
  };

  const handleRejectCourse = async (courseId, reason) => {
    try {
      const r = await AdminApi.rejectCourse(token, courseId, reason);
      if (r.success) { showNotification('success','Course Rejected','Instructor notified.'); fetchCourses(); }
      else showNotification('error','Rejection Failed', r.message || 'Failed.');
    } catch(err) { showNotification('error','Rejection Failed',err.message); }
  };

  const fetchNavBadges = useCallback(async () => {
    if (!token) return;
    try {
      const statsRes = await AdminApi.getContactStats(token);
      if (statsRes.success) setContactStats(statsRes.data);
    } catch (err) {
      console.error('Contact stats fetch error:', err);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchNavBadges(), fetchSystemHealth()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchNavBadges, fetchSystemHealth]);

  // Fetch Mentorship Data - defined before the useEffect that uses it
  const fetchMentorshipData = useCallback(async () => {
    if (!token) return;
    setMentorshipLoading(true);
    try {
      const [statsRes, mentorsRes, sessionsRes] = await Promise.all([
        AdminApi.getMentorshipStats(token),
        AdminApi.getMentorsAdmin(token, { ...mentorFilter, limit: 50 }),
        AdminApi.getMentoringSessions(token, sessionFilter)
      ]);
      if (statsRes.success) setMentorshipStats(statsRes.data);
      if (mentorsRes.success) setMentors(mentorsRes.data.mentors || mentorsRes.data);
      if (sessionsRes.success) setMentoringSessions(sessionsRes.data.sessions || sessionsRes.data);
    } catch (err) {
      console.error('Mentorship data fetch error:', err);
    } finally {
      setMentorshipLoading(false);
    }
  }, [token, mentorFilter, sessionFilter]);

  // Handle session status update
  const handleUpdateSessionStatus = async (sessionId, status) => {
    if (!token) return;
    try {
      const response = await AdminApi.updateSessionStatus(token, sessionId, status);
      if (response.success) {
        showNotification('success', 'Session Updated', `Session status updated to ${status}.`);
        fetchMentorshipData();
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update session.');
      }
    } catch (err) {
      showNotification('error', 'Update Failed', err.message || 'An error occurred.');
    }
  };

  // Fetch Contact Messages
  const fetchContactData = useCallback(async () => {
    if (!token) return;
    setContactLoading(true);
    try {
      const [statsRes, messagesRes] = await Promise.all([
        AdminApi.getContactStats(token),
        AdminApi.getContactMessages(token, contactFilter)
      ]);
      if (statsRes.success) setContactStats(statsRes.data);
      if (messagesRes.success) setContactMessages(messagesRes.data.messages || []);
    } catch (err) {
      console.error('Contact data fetch error:', err);
    } finally {
      setContactLoading(false);
    }
  }, [token, contactFilter]);

  // Handle Contact Message Actions
  const handleUpdateMessageStatus = async (messageId, status) => {
    if (!token) return;
    try {
      const response = await AdminApi.updateContactMessage(token, messageId, { status });
      if (response.success) {
        showNotification('success', 'Message Updated', `Message status updated to ${status}.`);
        fetchContactData();
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update message.');
      }
    } catch (err) {
      showNotification('error', 'Update Failed', err.message || 'An error occurred.');
    }
  };

  const handleArchiveMessage = async (messageId) => {
    if (!token) return;
    try {
      const response = await AdminApi.archiveContactMessage(token, messageId);
      if (response.success) {
        showNotification('success', 'Message Archived', 'Message has been archived.');
        fetchContactData();
        setSelectedMessage(null);
      }
    } catch (err) {
      showNotification('error', 'Archive Failed', err.message || 'An error occurred.');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!token) return;
    try {
      const response = await AdminApi.deleteContactMessage(token, messageId);
      if (response.success) {
        showNotification('success', 'Message Deleted', 'Message has been deleted.');
        fetchContactData();
        setSelectedMessage(null);
      }
    } catch (err) {
      showNotification('error', 'Delete Failed', err.message || 'An error occurred.');
    }
  };

  const handleReplyToMessage = async (messageId) => {
    if (!token || !replyText.trim()) return;
    try {
      const response = await AdminApi.updateContactMessage(token, messageId, { 
        responseMessage: replyText,
        status: 'responded'
      });
      if (response.success) {
        showNotification('success', 'Reply Sent', 'Your response has been recorded.');
        setReplyText('');
        fetchContactData();
      }
    } catch (err) {
      showNotification('error', 'Reply Failed', err.message || 'An error occurred.');
    }
  };

  // QoreTalks Guest Applications Functions
  const fetchGuestApplications = useCallback(async () => {
    if (!token) return;
    setQoretalksLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
      const response = await fetch(`${API_BASE}/api/qoretalks/guest-applications?status=${applicationFilter.status}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setGuestApplications(data.data || []);
        setApplicationStats(data.stats || {});
      }
    } catch (err) {
      console.error('Guest applications fetch error:', err);
      showNotification('error', 'Fetch Failed', 'Failed to load guest applications.');
    } finally {
      setQoretalksLoading(false);
    }
  }, [token, applicationFilter]);

  const handleUpdateApplication = async (applicationId, status, notes) => {
    if (!token) return;
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
      const response = await fetch(`${API_BASE}/api/qoretalks/guest-applications/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, notes })
      });
      const data = await response.json();
      
      if (data.success) {
        showNotification('success', 'Application Updated', `Application status updated to ${status}.`);
        fetchGuestApplications();
        setSelectedApplication(null);
      } else {
        showNotification('error', 'Update Failed', data.message || 'Failed to update application.');
      }
    } catch (err) {
      showNotification('error', 'Update Failed', err.message || 'An error occurred.');
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!token) return;
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
      const response = await fetch(`${API_BASE}/api/qoretalks/guest-applications/${applicationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        showNotification('success', 'Application Deleted', 'Application has been deleted.');
        fetchGuestApplications();
        setSelectedApplication(null);
      } else {
        showNotification('error', 'Delete Failed', data.message || 'Failed to delete application.');
      }
    } catch (err) {
      showNotification('error', 'Delete Failed', err.message || 'An error occurred.');
    }
  };

  // Tab-specific loading
  useEffect(() => {
    if (activeTab === 'qoreacademy' && activeSubTab === 'users') fetchUsers();
    if (activeTab === 'qoreacademy' && activeSubTab === 'courses') fetchCourses();
    if (activeTab === 'qoreacademy' && activeSubTab === 'elearning') fetchElearningData();
    if (activeTab === 'overview') {
      fetchDashboard();
      fetchCommandInbox();
      fetchSystemHealth();
    }

    if (activeTab === 'financial') fetchFinancialData();
    if (activeTab === 'ai-orchestrator' && activeSubTab === 'nova') fetchAgentData();
    if (activeTab === 'ai-orchestrator' && activeSubTab === 'atlas') fetchAtlasData();
    if (activeTab === 'qoreconnect' && activeSubTab === 'mentorship') fetchMentorshipData();
    if (activeTab === 'messages') fetchContactData();
    if (activeTab === 'overview') fetchNavBadges();
    if (activeTab === 'qoretalks' && activeSubTab === 'guests') fetchGuestApplications();
    if (activeTab === 'activity') fetchActivityLogs();
    if (activeTab === 'system-health') fetchSystemHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeSubTab]);

  // Mentorship Actions
  const handleApproveMentor = async (mentorId) => {
    if (!token) return;
    setProcessingMentor(mentorId);
    try {
      const response = await AdminApi.approveMentor(token, mentorId);
      if (response.success) {
        showNotification('success', 'Mentor Approved', 'Mentor has been approved successfully.');
        fetchMentorshipData();
      } else {
        showNotification('error', 'Approval Failed', response.message || 'Failed to approve mentor.');
      }
    } catch (err) {
      showNotification('error', 'Approval Failed', err.message || 'An error occurred.');
    } finally {
      setProcessingMentor(null);
    }
  };

  const handleRejectMentor = async (mentorId, reason = '') => {
    if (!token) return;
    setProcessingMentor(mentorId);
    try {
      const response = await AdminApi.rejectMentor(token, mentorId, reason);
      if (response.success) {
        showNotification('success', 'Mentor Rejected', 'Mentor application has been rejected.');
        fetchMentorshipData();
      } else {
        showNotification('error', 'Rejection Failed', response.message || 'Failed to reject mentor.');
      }
    } catch (err) {
      showNotification('error', 'Rejection Failed', err.message || 'An error occurred.');
    } finally {
      setProcessingMentor(null);
    }
  };

  const handleUpdateMentorStatus = async (mentorId, status, reason = '') => {
    if (!token) return;
    setProcessingMentor(mentorId);
    try {
      const response = await AdminApi.updateMentorStatus(token, mentorId, status, reason);
      if (response.success) {
        showNotification('success', 'Status Updated', `Mentor status updated to ${status}.`);
        fetchMentorshipData();
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update status.');
      }
    } catch (err) {
      showNotification('error', 'Update Failed', err.message || 'An error occurred.');
    } finally {
      setProcessingMentor(null);
    }
  };

  const handleUpdateRequestStatus = async (requestId, status, reason = '') => {
    if (!token) return;
    try {
      const response = await AdminApi.updateMentorshipRequestStatus(token, requestId, status, reason);
      if (response.success) {
        showNotification('success', 'Request Updated', `Mentorship request ${status}.`);
        fetchMentorshipData();
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update request.');
      }
    } catch (err) {
      showNotification('error', 'Update Failed', err.message || 'An error occurred.');
    }
  };

  // ============================================
  // E-LEARNING MANAGEMENT
  // ============================================
  
  // Fetch E-Learning data
  const fetchElearningData = useCallback(async () => {
    if (!token) return;
    setPathsLoading(true);
    try {
      // Fetch real data from backend
      const [statsRes, pathsRes, userStatsRes, globalLimitsRes] = await Promise.all([
        AdminApi.getElearningStats(token),
        AdminApi.getGeneratedPaths(token, { page: 1, limit: 20 }),
        AdminApi.getUserGenerationStats(token, { page: 1, limit: 20 }),
        AdminApi.getGlobalGenerationLimits(token)
      ]);
      
      if (statsRes.success) {
        setElearningStats(statsRes.data);
      }
      
      if (pathsRes.success) {
        setGeneratedPaths(pathsRes.data.paths || []);
      }
      
      if (userStatsRes.success) {
        setUserGenerationStats(userStatsRes.data.users || []);
      }
      
      if (globalLimitsRes.success) {
        setGenerationLimits({
          daily: globalLimitsRes.data.daily || 5,
          monthly: globalLimitsRes.data.monthly || 50,
          unlimited: globalLimitsRes.data.unlimited || false
        });
      }
    } catch (err) {
      console.error('E-Learning fetch error:', err);
      showNotification('error', 'Fetch Failed', 'Failed to load e-learning data.');
    } finally {
      setPathsLoading(false);
    }
  }, [token]);

  // View path details
  const handleViewPath = (path) => {
    setSelectedPath(path);
    setPathDetailModal(true);
  };

  // Delete generated path
  const handleDeletePath = (pathId, pathName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Learning Path',
      message: `Are you sure you want to delete this learning path for \"${pathName}\"? This action cannot be undone.`,
      danger: true,
      confirmText: 'Delete',
      onConfirm: () => confirmDeletePath(pathId)
    });
  };

  const confirmDeletePath = async (pathId) => {
    setConfirmModal({ isOpen: false });
    try {
      const response = await AdminApi.deleteLearningPath(token, pathId);
      if (response.success) {
        showNotification('success', 'Path Deleted', 'Learning path has been deleted successfully.');
        setGeneratedPaths(prev => prev.filter(p => p._id !== pathId));
      } else {
        showNotification('error', 'Delete Failed', response.message || 'Failed to delete path.');
      }
    } catch (err) {
      showNotification('error', 'Delete Failed', err.message || 'Failed to delete path.');
    }
  };

  // Edit user generation limits
  const handleEditUserLimits = (userStat) => {
    setSelectedUserForLimits(userStat);
    setCustomLimits({
      daily: userStat.dailyLimit,
      monthly: userStat.monthlyLimit,
      unlimited: userStat.unlimited
    });
    setEditLimitsModal(true);
  };

  const handleSaveLimits = async () => {
    if (!selectedUserForLimits) return;
    try {
      const response = await AdminApi.updateUserGenerationLimits(token, selectedUserForLimits.userId, customLimits);
      if (response.success) {
        showNotification('success', 'Limits Updated', `Generation limits updated for ${selectedUserForLimits.name}.`);
        
        // Update local state
        setUserGenerationStats(prev => prev.map(u => 
          u.userId === selectedUserForLimits.userId 
            ? { ...u, dailyLimit: customLimits.daily, monthlyLimit: customLimits.monthly, unlimited: customLimits.unlimited }
            : u
        ));
        
        setEditLimitsModal(false);
        setSelectedUserForLimits(null);
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update limits.');
      }
    } catch (err) {
      showNotification('error', 'Update Failed', err.message || 'Failed to update limits.');
    }
  };

  // Update global generation limits
  const handleUpdateGlobalLimits = async () => {
    try {
      const payload = {
        ...generationLimits,
        applyToAllUsers: true // Apply to all existing users
      };
      const response = await AdminApi.updateGlobalGenerationLimits(token, payload);
      if (response.success) {
        const message = response.data?.usersUpdated 
          ? `Global limits updated and applied to ${response.data.usersUpdated} users`
          : 'Global limits updated';
        showNotification('success', 'Global Limits Updated', message);
        // Refresh user stats to see updated limits
        fetchElearningData();
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update global limits.');
      }
    } catch (err) {
      showNotification('error', 'Update Failed', err.message || 'Failed to update global limits.');
    }
  };

  // Reset user's daily/monthly usage
  const handleResetUserUsage = async (userId, type) => {
    try {
      const response = await AdminApi.resetUserGenerationUsage(token, userId, type);
      if (response.success) {
        showNotification('success', 'Usage Reset', `${type} usage has been reset for this user.`);
        fetchElearningData();
      } else {
        showNotification('error', 'Reset Failed', response.message || 'Failed to reset usage.');
      }
    } catch (err) {
      showNotification('error', 'Reset Failed', err.message || 'Failed to reset usage.');
    }
  };

  // Fetch AI Agent data - NOVA v3.0
  const fetchAgentData = useCallback(async () => {
    if (!token) return;
    setAgentLoading(true);
    try {
      const [statusRes, healthRes, subAgentsRes, activityRes] = await Promise.all([
        AdminApi.getAgentStatus(token),
        AdminApi.getAgentHealth(token),
        AdminApi.getSubAgents(token),
        AdminApi.getAgentActivity(token, 20)
      ]);
      if (statusRes.success) {
        setAgentStatus(statusRes.data);
        setAutonomousMode(statusRes.data.autonomyLevel >= 0.9);
      }
      if (healthRes.success) setAgentHealth(healthRes.data);
      if (subAgentsRes.success) setSubAgents(subAgentsRes.data);
      if (activityRes.success) setAgentActivity(activityRes.data);
    } catch (err) {
      console.error('Agent data fetch error:', err);
    } finally {
      setAgentLoading(false);
    }
  }, [token]);

  // Fetch Atlas Engagement Data
  const fetchAtlasData = useCallback(async () => {
    if (!token) return;
    setAtlasLoading(true);
    try {
      const [dashboardRes, logsRes, atRiskRes] = await Promise.all([
        AdminApi.getAtlasDashboard(token),
        AdminApi.getAtlasLogs(token, 20),
        AdminApi.getAtlasAtRiskUsers(token)
      ]);
      
      if (dashboardRes.success) setAtlasData(dashboardRes.data);
      if (logsRes.success) setAtlasLogs(logsRes.data);
      if (atRiskRes.success) setAtRiskUsers(atRiskRes.data);
    } catch (err) {
      console.error('Atlas data fetch error:', err);
    } finally {
      setAtlasLoading(false);
    }
  }, [token]);

  // Execute Atlas Action
  const executeAtlasAction = async (action, params = {}) => {
    if (!token) return;
    setAtlasActionRunning(action);
    try {
      const response = await AdminApi.executeAtlasAction(token, action, params);
      
      if (response.success) {
        showNotification('success', 'Atlas Action Complete', `${action} executed successfully`);
        // Refresh Atlas data
        await fetchAtlasData();
      } else {
        showNotification('error', 'Atlas Action Failed', response.message || 'Unknown error');
      }
    } catch (err) {
      console.error('Atlas action error:', err);
      showNotification('error', 'Atlas Action Error', err.message);
    } finally {
      setAtlasActionRunning(null);
    }
  };

  // Toggle autonomous mode
  const toggleAutonomousMode = async () => {
    if (!token) return;
    try {
      const newMode = !autonomousMode;
      const response = await AdminApi.setAutonomousMode(token, newMode);
      if (response.success) {
        setAutonomousMode(newMode);
        setAgentStatus(prev => ({
          ...prev,
          autonomyLevel: response.data.autonomyLevel,
          status: response.data.status
        }));
      }
    } catch (err) {
      console.error('Error toggling autonomous mode:', err);
    }
  };

  // Delegate task to sub-agent
  const delegateToAgent = async (agentName, task) => {
    if (!token) return;
    setAgentLoading(true);
    try {
      const response = await AdminApi.delegateToSubAgent(token, agentName, task, {});
      if (response.success) {
        // Add the result to chat
        setAgentChat(prev => [...prev, {
          role: 'agent',
          content: `[${agentName}] ${response.data.result}`,
          timestamp: new Date()
        }]);
      }
    } catch (err) {
      console.error('Error delegating to sub-agent:', err);
    } finally {
      setAgentLoading(false);
    }
  };

  // Run AI Agent platform scan
  const runAgentScan = async () => {
    if (!token) return;
    setScanRunning(true);
    try {
      const response = await AdminApi.runAgentScan(token);
      if (response.success) {
        setAgentScan(response.data);
      }
    } catch (err) {
      console.error('Agent scan error:', err);
    } finally {
      setScanRunning(false);
    }
  };

  // Fetch AI insights
  const fetchAgentInsights = async (timeframe = '7d') => {
    if (!token) return;
    setAgentLoading(true);
    try {
      const response = await AdminApi.getAgentInsights(token, timeframe);
      if (response.success) {
        setAgentInsights(response.data);
      }
    } catch (err) {
      console.error('Agent insights error:', err);
    } finally {
      setAgentLoading(false);
    }
  };

  // Chat with AI Agent
  const sendAgentMessage = async () => {
    if (!chatInput.trim() || !token) return;
    
    const userMessage = { role: 'user', content: chatInput, timestamp: new Date() };
    setAgentChat(prev => [...prev, userMessage]);
    setChatInput('');
    setAgentLoading(true);
    
    try {
      const response = await AdminApi.chatWithAgent(token, chatInput);
      if (response.success) {
        const agentMessage = {
          role: 'agent',
          content: response.data.response,
          health: response.data.platformHealth,
          tools: response.data.toolCalls || [],
          timestamp: new Date()
        };
        setAgentChat(prev => [...prev, agentMessage]);
      }
    } catch (err) {
      console.error('Agent chat error:', err);
      setAgentChat(prev => [...prev, { role: 'error', content: 'Failed to get response', timestamp: new Date() }]);
    } finally {
      setAgentLoading(false);
    }
  };

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [agentChat]);

  // System health auto-refresh every 30 s
  useEffect(() => {
    if (activeTab !== 'system-health') return;
    fetchSystemHealth();
    const iv = setInterval(fetchSystemHealth, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchDashboard();
    if (activeTab === 'overview') await fetchCommandInbox();
    if (activeTab === 'qoreacademy' && activeSubTab === 'users') await fetchUsers();
    if (activeTab === 'qoreacademy' && activeSubTab === 'courses') await fetchCourses();
    if (activeTab === 'qoreacademy' && activeSubTab === 'elearning') await fetchElearningData();
    if (activeTab === 'activity') await fetchActivityLogs();

    if (activeTab === 'ai-orchestrator') await fetchAgentData();
    if (activeTab === 'qoreconnect' && activeSubTab === 'mentorship') await fetchMentorshipData();
    if (activeTab === 'qoretalks' && activeSubTab === 'guests') await fetchGuestApplications();
    if (activeTab === 'system-health') await fetchSystemHealth();
    if (activeTab === 'messages') await fetchContactData();
    await fetchNavBadges();
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation structure — functional sections only
  const navigationTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'messages', label: 'Messages', icon: Inbox },
    { id: 'activity', label: 'Activity', icon: Activity },
    {
      id: 'ai-orchestrator',
      label: 'AI Orchestrator',
      icon: Bot,
      sparkle: true,
      subTabs: [
        { id: 'nova', label: 'Nova Dashboard', icon: Bot },
        { id: 'atlas', label: 'Atlas Engagement', icon: Heart },
        { id: 'pulse', label: 'Pulse Talks', icon: Radio },
      ],
    },
    {
      id: 'qoreacademy',
      label: 'QoreAcademy',
      icon: GraduationCap,
      subTabs: [
        { id: 'users', label: 'Users', icon: Users },
        { id: 'courses', label: 'Courses', icon: BookOpen },
        { id: 'elearning', label: 'E-Learning', icon: Monitor },
        { id: 'labs', label: 'Labs', icon: FlaskConical },
        { id: 'certificates', label: 'Certificates', icon: AwardIcon },
        { id: 'career', label: 'Career', icon: Briefcase },
        { id: 'tutor', label: 'AI Tutor', icon: Bot },
      ],
    },
    {
      id: 'qoretalks',
      label: 'QoreTalks',
      icon: Radio,
      subTabs: [
        { id: 'episodes', label: 'Episodes', icon: Mic },
        { id: 'livestream', label: 'Livestream', icon: Video },
        { id: 'guests', label: 'Guests', icon: UserPlus },
        { id: 'subscribers', label: 'Subscribers', icon: Mail },
      ],
    },
    {
      id: 'qoreconnect',
      label: 'QoreConnect',
      icon: Users,
      subTabs: [
        { id: 'community', label: 'Community', icon: Users },
        { id: 'forum', label: 'Forum', icon: MessageSquare },
        { id: 'mentorship', label: 'Mentorship', icon: Heart },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
      ],
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: TrendingUp,
      subTabs: [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'users', label: 'Users', icon: Users },
        { id: 'content', label: 'Content', icon: BookOpen },
        { id: 'engagement', label: 'Engagement', icon: Activity },
        { id: 'reports', label: 'Reports', icon: FileText },
      ],
    },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'system-health', label: 'System Health', icon: Gauge },
    {
      id: 'financial',
      label: 'Finance',
      icon: DollarSign,
    },
    {
      id: 'support',
      label: 'Support',
      icon: Inbox,
      subTabs: [
        { id: 'tickets', label: 'Tickets', icon: Inbox },
        { id: 'templates', label: 'Templates', icon: Mail },
      ],
    },
  ];

  const navigateTo = (tabId, subTabId = null) => {
    setActiveTab(tabId);
    const tabConfig = navigationTabs.find((t) => t.id === tabId);
    if (subTabId) {
      setActiveSubTab(subTabId);
    } else if (tabConfig?.subTabs?.length) {
      setActiveSubTab(tabConfig.subTabs[0].id);
    } else {
      setActiveSubTab('');
    }
    if (isMobile) setSidebarOpen(false);
  };

  const handleTabChange = (tabId) => navigateTo(tabId);

  const unreadMessages = contactStats?.newMessages || 0;
  const pendingGuests =
    applicationStats?.pending ??
    guestApplications.filter((a) => a.status === 'pending').length;

  const navGroups = [
    {
      label: 'Command Center',
      items: [
        { id: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
        { id: 'messages', label: 'Messages', icon: <Inbox size={18} /> },
        { id: 'activity', label: 'Activity', icon: <Activity size={18} /> },
      ],
    },
    {
      label: 'AI & Automation',
      items: [
        { id: 'ai-orchestrator', label: 'AI Orchestrator', icon: <Bot size={18} />, badge: 'Nova' },
      ],
    },
    {
      label: 'Platform',
      items: [
        { id: 'qoreacademy', label: 'QoreAcademy', icon: <GraduationCap size={18} /> },
        { id: 'qoretalks', label: 'QoreTalks', icon: <Radio size={18} /> },
        { id: 'qoreconnect', label: 'QoreConnect', icon: <Users size={18} /> },
        { id: 'analytics', label: 'Analytics', icon: <TrendingUp size={18} /> },
        { id: 'financial', label: 'Finance', icon: <DollarSign size={18} /> },
      ],
    },
    {
      label: 'System',
      items: [
        { id: 'support', label: 'Support', icon: <Inbox size={18} /> },
        { id: 'system-health', label: 'System Health', icon: <Gauge size={18} /> },
      ],
    },
  ];

  const activeTabConfig = navigationTabs.find((t) => t.id === activeTab);
  const activeTabMeta = {
    title: activeTabConfig?.label || 'Admin Dashboard',
    subtitle: activeSubTab
      ? activeTabConfig?.subTabs?.find((s) => s.id === activeSubTab)?.label
      : undefined,
  };

  const shellSubTabs = (activeTabConfig?.subTabs || []).map((subTab) => ({
    ...subTab,
    icon: <subTab.icon size={16} />,
    count: subTab.id === 'guests' ? pendingGuests : 0,
  }));

  const adminInfo = {
    name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Administrator',
    avatar: (user?.firstName?.[0] || user?.name?.[0] || 'A').toUpperCase(),
  };

  // ============================================
  // COURSE ACTIONS
  // ============================================
  const handleViewCourse = (course) => {
    setViewCourse(course);
  };

  const handleEditCourse = (course) => {
    setEditCourse(course);
  };

  const handleSaveCourse = async (courseId, data) => {
    setSavingCourse(true);
    try {
      const response = await AdminApi.updateCourse(token, courseId, data);
      if (response.success) {
        showNotification('success', 'Course Updated', `"${data.title}" has been updated successfully.`);
        setEditCourse(null);
        fetchCourses();
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update course.');
      }
    } catch (err) {
      console.error('Update course error:', err);
      showNotification('error', 'Update Failed', err.message || 'An error occurred while updating the course.');
    } finally {
      setSavingCourse(false);
    }
  };

  const requestDeleteCourse = (course) => {
    const enrollmentCount = course.analytics?.enrollmentCount || course.enrollmentCount || 0;
    setConfirmModal({
      isOpen: true,
      title: 'Delete Course',
      message: enrollmentCount > 0 
        ? `"${course.title}" has ${enrollmentCount} student(s) enrolled. Deleting will also remove all enrollments. Are you sure?`
        : `Are you sure you want to delete "${course.title}"? This action cannot be undone.`,
      danger: true,
      confirmText: enrollmentCount > 0 ? 'Force Delete' : 'Delete',
      onConfirm: () => confirmDeleteCourse(course._id, course.title, enrollmentCount > 0)
    });
  };

  const confirmDeleteCourse = async (courseId, courseTitle, forceDelete = false) => {
    setConfirmModal({ isOpen: false });
    try {
      const response = await AdminApi.deleteCourse(token, courseId, forceDelete);
      if (response.success) {
        showNotification('success', 'Course Deleted', `"${courseTitle}" has been permanently deleted.`);
        fetchCourses();
      } else {
        // Check if it's an enrollment issue and offer force delete
        if (response.message?.includes('active enrollments') || response.enrollmentCount > 0) {
          const count = response.enrollmentCount || 'some';
          setConfirmModal({
            isOpen: true,
            title: 'Course Has Active Enrollments',
            message: `This course has ${count} student(s) enrolled. Do you want to force delete the course and remove all enrollments?`,
            danger: true,
            confirmText: 'Force Delete',
            onConfirm: () => confirmDeleteCourse(courseId, courseTitle, true)
          });
        } else {
          showNotification('error', 'Delete Failed', response.message || 'Failed to delete course.');
        }
      }
    } catch (err) {
      console.error('Delete course error:', err);
      // Check if error message mentions enrollments
      if (err.message?.includes('active enrollments') || err.message?.includes('enrolled')) {
        const match = err.message.match(/(\d+)\s*student/);
        const count = match ? match[1] : 'some';
        setConfirmModal({
          isOpen: true,
          title: 'Course Has Active Enrollments',
          message: `This course has ${count} student(s) enrolled. Do you want to force delete the course and remove all enrollments?`,
          danger: true,
          confirmText: 'Force Delete',
          onConfirm: () => confirmDeleteCourse(courseId, courseTitle, true)
        });
      } else {
        showNotification('error', 'Delete Failed', err.message || 'An error occurred while deleting the course.');
      }
    }
  };

  // ============================================
  // USER ACTIONS
  // ============================================
  const requestDeleteUser = (userToDelete) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete "${userToDelete.firstName} ${userToDelete.lastName}"? This action cannot be undone.`,
      danger: true,
      onConfirm: () => confirmDeleteUser(userToDelete._id, `${userToDelete.firstName} ${userToDelete.lastName}`)
    });
  };

  const confirmDeleteUser = async (userId, userName) => {
    setConfirmModal({ isOpen: false });
    try {
      const response = await AdminApi.deleteUser(token, userId);
      if (response.success) {
        showNotification('success', 'User Deleted', `"${userName}" has been permanently deleted.`);
        fetchUsers();
        setSelectedUser(null);
        setUserDetailModal(false);
      } else {
        showNotification('error', 'Delete Failed', response.message || 'Failed to delete user.');
      }
    } catch (err) {
      console.error('Delete user error:', err);
      showNotification('error', 'Delete Failed', err.message || 'An error occurred while deleting the user.');
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setUserDetailModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user });
    setEditUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      const response = await AdminApi.updateUser(token, editingUser._id, {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        role: editingUser.role,
        status: editingUser.status
      });
      if (response.success) {
        showNotification('success', 'User Updated', 'User information has been updated successfully.');
        fetchUsers();
        setEditUserModal(false);
        setEditingUser(null);
      } else {
        showNotification('error', 'Update Failed', response.message || 'Failed to update user.');
      }
    } catch (err) {
      console.error('Update user error:', err);
      showNotification('error', 'Update Failed', err.message || 'An error occurred while updating the user.');
    }
  };

  const handleBanUser = async (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Ban User',
      message: `Are you sure you want to ban "${user.firstName} ${user.lastName}"? They will not be able to access the platform.`,
      danger: true,
      confirmText: 'Ban User',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          const response = await AdminApi.updateUser(token, user._id, { status: 'banned' });
          if (response.success) {
            showNotification('success', 'User Banned', `"${user.firstName} ${user.lastName}" has been banned.`);
            fetchUsers();
            setSelectedUser(null);
            setUserDetailModal(false);
          } else {
            showNotification('error', 'Ban Failed', response.message || 'Failed to ban user.');
          }
        } catch (err) {
          showNotification('error', 'Ban Failed', err.message || 'An error occurred.');
        }
      }
    });
  };

  const handleActivateUser = async (user) => {
    try {
      const response = await AdminApi.updateUser(token, user._id, { status: 'active' });
      if (response.success) {
        showNotification('success', 'User Activated', `"${user.firstName} ${user.lastName}" has been activated.`);
        fetchUsers();
        if (selectedUser?._id === user._id) {
          setSelectedUser({ ...user, status: 'active' });
        }
      } else {
        showNotification('error', 'Activation Failed', response.message || 'Failed to activate user.');
      }
    } catch (err) {
      showNotification('error', 'Activation Failed', err.message || 'An error occurred.');
    }
  };

  const handleResetPassword = async (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Password',
      message: `Send a password reset email to "${user.email}"?`,
      confirmText: 'Send Reset Email',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          // Mock password reset - replace with actual API call
          await new Promise(resolve => setTimeout(resolve, 1000));
          showNotification('success', 'Reset Email Sent', `Password reset email has been sent to ${user.email}`);
        } catch (err) {
          showNotification('error', 'Reset Failed', err.message || 'An error occurred.');
        }
      }
    });
  };

  const handleAddUser = () => {
    setNewUser({ firstName: '', lastName: '', email: '', role: 'student', password: '' });
    setAddUserModal(true);
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
      showNotification('error', 'Validation Error', 'Please fill in all required fields.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      showNotification('error', 'Validation Error', 'Please enter a valid email address.');
      return;
    }

    // Password validation (at least 8 characters)
    if (newUser.password.length < 8) {
      showNotification('error', 'Validation Error', 'Password must be at least 8 characters long.');
      return;
    }

    try {
      const response = await AdminApi.createUser(token, newUser);
      if (response.success) {
        showNotification('success', 'User Created', `User "${newUser.firstName} ${newUser.lastName}" has been created successfully.`);
        fetchUsers();
        setAddUserModal(false);
        setNewUser({ firstName: '', lastName: '', email: '', role: 'student', password: '' });
      } else {
        showNotification('error', 'Creation Failed', response.message || 'Failed to create user.');
      }
    } catch (err) {
      console.error('Create user error:', err);
      showNotification('error', 'Creation Failed', err.message || 'An error occurred while creating the user.');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u._id));
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;
    
    setConfirmModal({
      isOpen: true,
      title: `Bulk ${bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)}`,
      message: `Are you sure you want to ${bulkAction} ${selectedUsers.length} user(s)?`,
      danger: bulkAction === 'delete' || bulkAction === 'ban',
      confirmText: `${bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)} Users`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          // Mock bulk action - replace with actual API call
          await new Promise(resolve => setTimeout(resolve, 1500));
          showNotification('success', 'Bulk Action Complete', `Successfully ${bulkAction}ed ${selectedUsers.length} user(s).`);
          setSelectedUsers([]);
          setBulkAction('');
          fetchUsers();
        } catch (err) {
          showNotification('error', 'Bulk Action Failed', err.message || 'An error occurred.');
        }
      }
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !dashboard) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const { overview, growth, usersByRole, recentUsers, recentEnrollments, recentActivity, topCourses } = dashboard || {};

  const pendingActions =
    (overview?.pendingInstructorApprovals || 0) + (overview?.pendingCourseApprovals || 0);

  const navGroupsWithCounts = navGroups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      if (item.id === 'overview') return { ...item, count: pendingActions, alert: true };
      if (item.id === 'messages') return { ...item, count: unreadMessages, alert: true };
      return item;
    }),
  }));

  const healthBannerOk = systemHealth
    ? !Object.values(systemHealth).some((m) => m?.status === 'critical' || m?.status === 'warning')
    : true;

  return (
    <div className="admin-dashboard">
      <Notification notifications={notifications} onDismiss={dismissNotification} />

      <AdminDashboardShell
        adminInfo={adminInfo}
        activeTab={activeTab}
        activeTabMeta={activeTabMeta}
        navGroups={navGroupsWithCounts}
        subTabs={shellSubTabs}
        activeSubTab={activeSubTab}
        onSubTabChange={setActiveSubTab}
        onTabChange={handleTabChange}
        sidebarCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((o) => !o)}
        isMobile={isMobile}
        onLogout={handleLogout}
        onRefresh={handleRefresh}
        loading={loading}
        error={error}
        onDismissError={() => setError(null)}
      >
        {activeTab === 'overview' && dashboard && (
          <AdminOverviewPanel
            dashboard={dashboard}
            user={user}
            systemHealth={systemHealth}
            contactStats={contactStats}
            healthBannerOk={healthBannerOk}
            onNavigate={navigateTo}
            inbox={commandInbox}
            crossLine={crossLine}
            onRunTool={runCommandTool}
            toolBusy={toolBusy}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'qoreacademy' && activeSubTab === 'users' && (
          <div className="tab-users-v2">
            {/* User Stats Header */}
            <section className="user-stats-header">
              <div className="user-stat-card">
                <div className="stat-icon-v2 primary">
                  <Users size={24} />
                </div>
                <div className="stat-content-v2">
                  <h3>{userTotal}</h3>
                  <p>Total Users</p>
                </div>
              </div>
              <div className="user-stat-card">
                <div className="stat-icon-v2 success">
                  <UserCheck size={24} />
                </div>
                <div className="stat-content-v2">
                  <h3>{users.filter(u => u.status === 'active').length}</h3>
                  <p>Active Users</p>
                </div>
              </div>
              <div className="user-stat-card">
                <div className="stat-icon-v2 warning">
                  <GraduationCap size={24} />
                </div>
                <div className="stat-content-v2">
                  <h3>{users.filter(u => u.role === 'student').length}</h3>
                  <p>Students</p>
                </div>
              </div>
              <div className="user-stat-card">
                <div className="stat-icon-v2 info">
                  <Award size={24} />
                </div>
                <div className="stat-content-v2">
                  <h3>{users.filter(u => u.role === 'instructor').length}</h3>
                  <p>Instructors</p>
                </div>
              </div>
            </section>

            {/* Control Bar */}
            <section className="user-control-bar">
              <div className="control-left">
                <h2>User Management</h2>
                <span className="result-count">{userTotal} users found</span>
              </div>
              <div className="control-right">
                <div className="search-box-v2">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search by name, email..."
                    value={userFilter.search}
                    onChange={(e) => setUserFilter(f => ({ ...f, search: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                  />
                  {userFilter.search && (
                    <button 
                      className="clear-search"
                      onClick={() => {
                        setUserFilter(f => ({ ...f, search: '' }));
                        fetchUsers();
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <select
                  className="filter-select-v2"
                  value={userFilter.role}
                  onChange={(e) => {
                    setUserFilter(f => ({ ...f, role: e.target.value }));
                    setUserPage(1);
                  }}
                >
                  <option value="">All Roles</option>
                  <option value="student">Students</option>
                  <option value="instructor">Instructors</option>
                  <option value="admin">Admins</option>
                </select>
                <button className="btn-primary-v2" onClick={handleAddUser}>
                  <UserPlus size={18} />
                  Add User
                </button>
              </div>
            </section>

            {/* Bulk Actions Bar */}
            {selectedUsers.length > 0 && (
              <section className="bulk-action-bar">
                <div className="bulk-info">
                  <input type="checkbox" checked={selectedUsers.length === users.length} onChange={toggleAllUsers} />
                  <span className="bulk-count"><Check size={14} /> {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected</span>
                </div>
                <div className="bulk-actions">
                  <button className="btn-bulk-action suspend" onClick={handleBulkSuspend}>
                    <Ban size={15} /> Suspend Selected
                  </button>
                  <button className="btn-bulk-action activate" onClick={handleBulkActivate}>
                    <CheckCircle size={15} /> Activate Selected
                  </button>
                  <button className="btn-bulk-action export" onClick={exportSelectedUsers}>
                    <Download size={15} /> Export CSV
                  </button>
                  <button className="btn-bulk-cancel" onClick={() => setSelectedUsers([])}><X size={15} /> Clear</button>
                </div>
              </section>
            )}

            {/* Users Table */}
            <section className="users-table-section">
              <div className="table-wrapper-v2">
                <table className="data-table-v2">
                  <thead>
                    <tr>
                      <th className="checkbox-col">
                        <input 
                          type="checkbox" 
                          checked={selectedUsers.length === users.length && users.length > 0}
                          onChange={toggleAllUsers}
                        />
                      </th>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Last Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map(u => (
                        <tr 
                          key={u._id}
                          className={selectedUsers.includes(u._id) ? 'selected' : ''}
                        >
                          <td className="checkbox-col">
                            <input 
                              type="checkbox"
                              checked={selectedUsers.includes(u._id)}
                              onChange={() => toggleUserSelection(u._id)}
                            />
                          </td>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar-small">
                                {(u.firstName || u.email).charAt(0).toUpperCase()}
                              </div>
                              <div className="user-info-cell">
                                <span className="user-name-cell">
                                  {u.firstName && u.lastName 
                                    ? `${u.firstName} ${u.lastName}` 
                                    : u.name || 'Unknown'}
                                </span>
                                <span className="user-email-cell">{u.email}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge-role ${u.role}`}>
                              {u.role === 'student' && <GraduationCap size={12} />}
                              {u.role === 'instructor' && <Award size={12} />}
                              {u.role === 'admin' && <Shield size={12} />}
                              {u.role}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge-v2 status-${u.status || 'active'}`}>
                              <div className={`status-dot ${u.status || 'active'}`}></div>
                              {u.status || 'active'}
                            </span>
                          </td>
                          <td>
                            <span className="date-text">{formatDate(u.createdAt)}</span>
                          </td>
                          <td>
                            <span className="date-text">
                              {u.lastLogin ? formatDate(u.lastLogin) : 'Never'}
                            </span>
                          </td>
                          <td>
                            <div className="action-btns-v2">
                              <button 
                                className="btn-action view"
                                title="View Details"
                                onClick={() => handleViewUser(u)}
                              >
                                <Eye size={16} />
                              </button>
                              <button 
                                className="btn-action edit"
                                title="Edit User"
                                onClick={() => handleEditUser(u)}
                              >
                                <Edit size={16} />
                              </button>
                              {(u.status === 'banned' || u.status === 'inactive' || u.status === 'pending') ? (
                                <button 
                                  className="btn-action activate"
                                  title="Activate User"
                                  onClick={() => handleActivateUser(u)}
                                >
                                  <CheckCircle size={16} />
                                </button>
                              ) : (
                                <button 
                                  className="btn-action ban"
                                  title="Ban User"
                                  onClick={() => handleBanUser(u)}
                                >
                                  <Ban size={16} />
                                </button>
                              )}
                              <button 
                                className="btn-action delete"
                                title="Delete User"
                                onClick={() => requestDeleteUser(u)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="empty-state-v2">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Pagination */}
            <section className="pagination-v2">
              <div className="pagination-info">
                Showing {users.length > 0 ? ((userPage - 1) * 10 + 1) : 0} to {Math.min(userPage * 10, userTotal)} of {userTotal} users
              </div>
              <div className="pagination-controls">
                <button 
                  className="btn-page"
                  disabled={userPage <= 1} 
                  onClick={() => setUserPage(p => p - 1)}
                >
                  <ChevronDown size={16} style={{ transform: 'rotate(90deg)' }} />
                  Previous
                </button>
                <div className="page-numbers">
                  {[...Array(Math.min(5, Math.ceil(userTotal / 10)))].map((_, i) => {
                    const pageNum = userPage <= 3 ? i + 1 : userPage - 2 + i;
                    if (pageNum > Math.ceil(userTotal / 10)) return null;
                    return (
                      <button
                        key={pageNum}
                        className={`btn-page-num ${userPage === pageNum ? 'active' : ''}`}
                        onClick={() => setUserPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button 
                  className="btn-page"
                  disabled={users.length < 10} 
                  onClick={() => setUserPage(p => p + 1)}
                >
                  Next
                  <ChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />
                </button>
              </div>
            </section>

            {/* User Detail Modal */}
            {userDetailModal && selectedUser && (
              <div className="modal-overlay" onClick={() => setUserDetailModal(false)}>
                <div className="modal-content-v2 user-detail-modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header-v2">
                    <div className="modal-title-section">
                      <div className="user-avatar-large">
                        {(selectedUser.firstName || selectedUser.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3>
                          {selectedUser.firstName && selectedUser.lastName 
                            ? `${selectedUser.firstName} ${selectedUser.lastName}` 
                            : selectedUser.name || 'Unknown User'}
                        </h3>
                        <p className="modal-subtitle">{selectedUser.email}</p>
                      </div>
                    </div>
                    <button className="modal-close-btn" onClick={() => setUserDetailModal(false)}>
                      <X size={20} />
                    </button>
                  </div>

                  <div className="modal-body-v2">
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Role</span>
                        <span className={`badge-role ${selectedUser.role}`}>
                          {selectedUser.role}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Status</span>
                        <span className={`status-badge-v2 status-${selectedUser.status || 'active'}`}>
                          <div className={`status-dot ${selectedUser.status || 'active'}`}></div>
                          {selectedUser.status || 'active'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Joined</span>
                        <span className="detail-value">{formatDate(selectedUser.createdAt)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Last Login</span>
                        <span className="detail-value">
                          {selectedUser.lastLogin ? formatDate(selectedUser.lastLogin) : 'Never'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">User ID</span>
                        <code className="detail-code">{selectedUser._id}</code>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h4>Activity Summary</h4>
                      <div className="activity-stats">
                        <div className="activity-stat-item">
                          <BookOpen size={18} />
                          <div>
                            <span className="stat-number">0</span>
                            <span className="stat-label">Courses Enrolled</span>
                          </div>
                        </div>
                        <div className="activity-stat-item">
                          <Award size={18} />
                          <div>
                            <span className="stat-number">0</span>
                            <span className="stat-label">Certificates</span>
                          </div>
                        </div>
                        <div className="activity-stat-item">
                          <MessageSquare size={18} />
                          <div>
                            <span className="stat-number">0</span>
                            <span className="stat-label">Forum Posts</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer-v2">
                    <button 
                      className="btn-secondary-v2"
                      onClick={() => handleResetPassword(selectedUser)}
                    >
                      <Mail size={16} />
                      Reset Password
                    </button>
                    <button 
                      className="btn-secondary-v2"
                      onClick={() => {
                        setUserDetailModal(false);
                        handleEditUser(selectedUser);
                      }}
                    >
                      <Edit size={16} />
                      Edit User
                    </button>
                    {(selectedUser.status === 'banned' || selectedUser.status === 'inactive' || selectedUser.status === 'pending') ? (
                      <button 
                        className="btn-success-v2"
                        onClick={() => handleActivateUser(selectedUser)}
                      >
                        <CheckCircle size={16} />
                        Activate User
                      </button>
                    ) : (
                      <button 
                        className="btn-danger-v2"
                        onClick={() => handleBanUser(selectedUser)}
                      >
                        <Ban size={16} />
                        Ban User
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Edit User Modal */}
            {editUserModal && editingUser && (
              <div className="modal-overlay" onClick={() => setEditUserModal(false)}>
                <div className="modal-content-v2 edit-user-modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header-v2">
                    <h3>Edit User</h3>
                    <button className="modal-close-btn" onClick={() => setEditUserModal(false)}>
                      <X size={20} />
                    </button>
                  </div>

                  <div className="modal-body-v2">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>First Name</label>
                        <input
                          type="text"
                          value={editingUser.firstName || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                          className="form-input-v2"
                        />
                      </div>
                      <div className="form-group">
                        <label>Last Name</label>
                        <input
                          type="text"
                          value={editingUser.lastName || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                          className="form-input-v2"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={editingUser.email || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                          className="form-input-v2"
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Role</label>
                        <select
                          value={editingUser.role || 'student'}
                          onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                          className="form-select-v2"
                        >
                          <option value="student">Student</option>
                          <option value="instructor">Instructor</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={editingUser.status || 'active'}
                          onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                          className="form-select-v2"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="banned">Banned</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer-v2">
                    <button 
                      className="btn-secondary-v2"
                      onClick={() => setEditUserModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn-primary-v2"
                      onClick={handleSaveUser}
                    >
                      <Save size={16} />
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Add User Modal */}
            {addUserModal && (
              <div className="modal-overlay" onClick={() => setAddUserModal(false)}>
                <div className="modal-content-v2" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header-v2">
                    <div className="modal-title-section">
                      <div className="user-avatar-large" style={{ background: 'var(--admin-success)' }}>
                        <UserPlus size={28} />
                      </div>
                      <div>
                        <h3>Add New User</h3>
                        <p className="modal-subtitle">Create a new user account</p>
                      </div>
                    </div>
                    <button className="modal-close-btn" onClick={() => setAddUserModal(false)}>
                      <X size={20} />
                    </button>
                  </div>
                  <div className="modal-body-v2">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>First Name *</label>
                        <input 
                          type="text" 
                          className="form-input-v2"
                          placeholder="Enter first name"
                          value={newUser.firstName}
                          onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Last Name *</label>
                        <input 
                          type="text" 
                          className="form-input-v2"
                          placeholder="Enter last name"
                          value={newUser.lastName}
                          onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                        />
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Email Address *</label>
                        <input 
                          type="email" 
                          className="form-input-v2"
                          placeholder="user@example.com"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Password *</label>
                        <input 
                          type="password" 
                          className="form-input-v2"
                          placeholder="Minimum 8 characters"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Role</label>
                        <select 
                          className="form-select-v2"
                          value={newUser.role}
                          onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                        >
                          <option value="student">Student</option>
                          <option value="instructor">Instructor</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer-v2">
                    <button className="btn-secondary-v2" onClick={() => setAddUserModal(false)}>
                      <X size={18} />
                      Cancel
                    </button>
                    <button className="btn-success-v2" onClick={handleCreateUser}>
                      <UserPlus size={18} />
                      Create User
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'qoreacademy' && activeSubTab === 'courses' && (
          <div className="tab-courses">
            <div className="tab-header">
              <h2>Course Management</h2>
              <div className="filters">
                <div className="search-box">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={courseFilter.search}
                    onChange={(e) => setCourseFilter(f => ({ ...f, search: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && fetchCourses()}
                  />
                </div>
                <select
                  value={courseFilter.status}
                  onChange={(e) => {
                    setCourseFilter(f => ({ ...f, status: e.target.value }));
                    setCoursePage(1);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="pending-approval">Pending</option>
                </select>
              </div>
            </div>

            <div className="courses-grid">
              {courses.length > 0 ? (
                courses.map(c => (
                  <div key={c._id} className="course-card">
                    {c.thumbnail && (
                      <div className="course-thumbnail-preview">
                        <img src={c.thumbnail} alt={c.title} />
                      </div>
                    )}
                    <div className="course-header">
                      <h4>{c.title}</h4>
                      <span className={`status ${c.status || (c.isPublished ? 'published' : 'draft')}`}>
                        {c.status || (c.isPublished ? 'Published' : 'Draft')}
                      </span>
                    </div>
                    <p className="course-desc">{c.description?.substring(0, 100)}...</p>
                    <div className="course-meta">
                      <span><Users size={14} /> {c.analytics?.enrollmentCount || c.enrollmentCount || 0} enrolled</span>
                      <span><Layers size={14} /> {c.lessons?.length || 0} lessons</span>
                      <span><Tag size={14} /> {c.category || 'General'}</span>
                    </div>
                    <div className="course-meta">
                      <span><Award size={14} /> {c.level || 'All Levels'}</span>
                      {c.price > 0 ? (
                        <span className="price"><DollarSign size={14} /> {c.price}</span>
                      ) : (
                        <span className="free-badge">Free</span>
                      )}
                    </div>
                    <div className="course-actions">
                      <button className="btn-small primary" onClick={() => handleViewCourse(c)}>
                        <Eye size={14} /> View
                      </button>
                      {(c.status === 'pending-approval' || c.status === 'pending') && (
                        <button className="btn-small approve-btn" onClick={() => handleOpenApprovalModal(c)}>
                          <CheckCircle size={14} /> Review
                        </button>
                      )}
                      <button className="btn-small" onClick={() => handleEditCourse(c)}>
                        <Edit size={14} /> Edit
                      </button>
                      <button className="btn-small danger" onClick={() => requestDeleteCourse(c)}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-state">No courses found</p>
              )}
            </div>

            <div className="pagination">
              <span>{courseTotal} courses total</span>
              <div className="pagination-btns">
                <button disabled={coursePage <= 1} onClick={() => setCoursePage(p => p - 1)}>Prev</button>
                <span>Page {coursePage}</span>
                <button disabled={courses.length < 10} onClick={() => setCoursePage(p => p + 1)}>Next</button>
              </div>
            </div>
            <CourseApprovalModal
              course={approvalModal.course}
              isOpen={approvalModal.isOpen}
              onClose={() => setApprovalModal({ isOpen: false, course: null })}
              onApprove={handleApproveCourse}
              onReject={handleRejectCourse}
            />
          </div>
        )}

        {/* E-Learning Tab - AI Course Generation Management */}
        {activeTab === 'qoreacademy' && activeSubTab === 'elearning' && (
          <div className="tab-elearning">
            <div className="tab-header">
              <div>
                <h2><Brain size={24} /> AI Learning Path Generation</h2>
                <p>Manage AI-powered course generation, user limits, and track generation activity</p>
              </div>
              <button onClick={fetchElearningData} className="btn-refresh" disabled={pathsLoading}>
                <RefreshCw size={18} className={pathsLoading ? 'spin' : ''} />
                <span>Refresh</span>
              </button>
            </div>

            {/* Stats Overview */}
            {elearningStats && (
              <div className="elearning-stats-grid">
                <div className="stat-card">
                  <div className="stat-icon purple">
                    <Sparkles size={24} />
                  </div>
                  <div className="stat-content">
                    <h4>Total Paths Generated</h4>
                    <p className="stat-value">{elearningStats.totalPathsGenerated.toLocaleString()}</p>
                    <span className="stat-change positive">+{elearningStats.pathsThisMonth} this month</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon blue">
                    <Users size={24} />
                  </div>
                  <div className="stat-content">
                    <h4>Active Users</h4>
                    <p className="stat-value">{elearningStats.activeUsers}</p>
                    <span className="stat-meta">Using AI generation</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon green">
                    <Zap size={24} />
                  </div>
                  <div className="stat-content">
                    <h4>Avg Generation Time</h4>
                    <p className="stat-value">{elearningStats.avgGenerationTime}</p>
                    <span className="stat-meta">Lightning fast ⚡</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon orange">
                    <Target size={24} />
                  </div>
                  <div className="stat-content">
                    <h4>Success Rate</h4>
                    <p className="stat-value">{elearningStats.successRate}%</p>
                    <span className="stat-change positive">+2.3% vs last month</span>
                  </div>
                </div>
              </div>
            )}

            {/* Global Limits Configuration */}
            <div className="limits-config-section">
              <div className="section-header">
                <h3><Settings size={20} /> Global Generation Limits</h3>
                <button className="btn-small primary" onClick={handleUpdateGlobalLimits}>
                  <Save size={14} /> Save Changes
                </button>
              </div>
              <div className="limits-grid">
                <div className="limit-input-group">
                  <label>Daily Limit (per user)</label>
                  <input 
                    type="number" 
                    min="1" 
                    value={generationLimits.daily}
                    onChange={e => setGenerationLimits(prev => ({ ...prev, daily: parseInt(e.target.value) || 1 }))}
                  />
                  <span className="input-hint">Max paths a user can generate per day</span>
                </div>
                <div className="limit-input-group">
                  <label>Monthly Limit (per user)</label>
                  <input 
                    type="number" 
                    min="1" 
                    value={generationLimits.monthly}
                    onChange={e => setGenerationLimits(prev => ({ ...prev, monthly: parseInt(e.target.value) || 1 }))}
                  />
                  <span className="input-hint">Max paths a user can generate per month</span>
                </div>
                <div className="limit-input-group">
                  <label>Default Mode</label>
                  <select 
                    value={generationLimits.unlimited ? 'unlimited' : 'limited'}
                    onChange={e => setGenerationLimits(prev => ({ ...prev, unlimited: e.target.value === 'unlimited' }))}
                  >
                    <option value="limited">Limited</option>
                    <option value="unlimited">Unlimited</option>
                  </select>
                  <span className="input-hint">Default limit mode for new users</span>
                </div>
              </div>
            </div>

            {/* User Generation Stats */}
            <div className="user-gen-stats-section">
              <div className="section-header">
                <h3><Users size={20} /> User Generation Statistics</h3>
                <div className="header-actions">
                  <input 
                    type="text"
                    placeholder="Search users..."
                    value={generationHistoryFilter.user}
                    onChange={e => setGenerationHistoryFilter(prev => ({ ...prev, user: e.target.value }))}
                    className="search-input-small"
                  />
                </div>
              </div>

              <div className="data-table-v2">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Total Generated</th>
                      <th>Daily Limit</th>
                      <th>Monthly Limit</th>
                      <th>Used Today</th>
                      <th>Used This Month</th>
                      <th>Last Generation</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userGenerationStats.length > 0 ? (
                      userGenerationStats
                        .filter(u => !generationHistoryFilter.user || 
                          u.name.toLowerCase().includes(generationHistoryFilter.user.toLowerCase()) ||
                          u.email.toLowerCase().includes(generationHistoryFilter.user.toLowerCase()))
                        .map(userStat => (
                        <tr key={userStat.userId}>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar">{userStat.name.charAt(0)}</div>
                              <span>{userStat.name}</span>
                            </div>
                          </td>
                          <td>{userStat.email}</td>
                          <td><strong>{userStat.totalGenerations}</strong></td>
                          <td>
                            {userStat.unlimited ? (
                              <span className="badge-unlimited">Unlimited</span>
                            ) : (
                              <span>{userStat.dailyLimit}</span>
                            )}
                          </td>
                          <td>
                            {userStat.unlimited ? (
                              <span className="badge-unlimited">Unlimited</span>
                            ) : (
                              <span>{userStat.monthlyLimit}</span>
                            )}
                          </td>
                          <td>
                            <span className={`usage-badge ${userStat.usedToday >= userStat.dailyLimit ? 'at-limit' : ''}`}>
                              {userStat.usedToday}/{userStat.dailyLimit}
                            </span>
                          </td>
                          <td>
                            <span className={`usage-badge ${userStat.usedThisMonth >= userStat.monthlyLimit ? 'at-limit' : ''}`}>
                              {userStat.usedThisMonth}/{userStat.monthlyLimit}
                            </span>
                          </td>
                          <td>{formatDate(userStat.lastGeneration)}</td>
                          <td>
                            <div className="action-btns-v2">
                              <button 
                                className="btn-icon-small" 
                                title="Edit Limits"
                                onClick={() => handleEditUserLimits(userStat)}
                              >
                                <Settings size={14} />
                              </button>
                              <button 
                                className="btn-icon-small"
                                title="Reset Daily"
                                onClick={() => handleResetUserUsage(userStat.userId, 'daily')}
                              >
                                <RefreshCw size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                          No generation statistics available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Generated Paths History */}
            <div className="generated-paths-section">
              <div className="section-header">
                <h3><BookOpen size={20} /> Generated Learning Paths</h3>
                <div className="header-actions">
                  <select 
                    value={generationHistoryFilter.status}
                    onChange={e => setGenerationHistoryFilter(prev => ({ ...prev, status: e.target.value }))}
                    className="filter-select"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                  </select>
                  <select 
                    value={generationHistoryFilter.dateRange}
                    onChange={e => setGenerationHistoryFilter(prev => ({ ...prev, dateRange: e.target.value }))}
                    className="filter-select"
                  >
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="90d">Last 90 Days</option>
                    <option value="all">All Time</option>
                  </select>
                </div>
              </div>

              <div className="paths-grid">
                {pathsLoading ? (
                  <div className="loading-state">
                    <RefreshCw className="spin" size={32} />
                    <p>Loading paths...</p>
                  </div>
                ) : generatedPaths.length > 0 ? (
                  generatedPaths
                    .filter(p => generationHistoryFilter.status === 'all' || p.status === generationHistoryFilter.status)
                    .map(path => (
                    <div key={path._id} className="path-card">
                      <div className="path-header">
                        <div>
                          <h4>{path.targetRole}</h4>
                          <span className={`status-badge-v2 ${path.status}`}>{path.status}</span>
                        </div>
                        <span className="generation-time">
                          <Zap size={12} /> {path.generationTime}
                        </span>
                      </div>
                      
                      <div className="path-user">
                        <Users size={14} />
                        <span>{path.user.firstName} {path.user.lastName}</span>
                        <span className="user-email">({path.user.email})</span>
                      </div>

                      <div className="path-stats-mini">
                        <div className="stat-mini">
                          <BookOpen size={14} />
                          <span>{path.totalSkills} Skills</span>
                        </div>
                        <div className="stat-mini">
                          <CheckCircle size={14} />
                          <span>{path.completedSkills} Completed</span>
                        </div>
                        <div className="stat-mini">
                          <Clock size={14} />
                          <span>{path.totalHours}h Total</span>
                        </div>
                      </div>

                      <div className="path-meta">
                        <span>Created: {formatDate(path.createdAt)}</span>
                        <span>Est. Completion: {formatDate(path.estimatedCompletion)}</span>
                      </div>

                      <div className="path-actions">
                        <button className="btn-small primary" onClick={() => handleViewPath(path)}>
                          <Eye size={14} /> View Details
                        </button>
                        <button className="btn-small danger" onClick={() => handleDeletePath(path._id, path.targetRole)}>
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Brain size={48} />
                    <p>No learning paths generated yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top Categories */}
            {elearningStats?.topCategories && (
              <div className="top-categories-section">
                <h3><TrendingUp size={20} /> Top Categories</h3>
                <div className="categories-chart">
                  {elearningStats.topCategories.map((cat, idx) => (
                    <div key={idx} className="category-bar">
                      <div className="category-info">
                        <span className="category-name">{cat.name}</span>
                        <span className="category-count">{cat.count} paths</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${(cat.count / elearningStats.totalPathsGenerated) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="tab-activity">
            <div className="tab-header">
              <h2>Activity Logs</h2>
              <button onClick={fetchActivityLogs} className="btn-refresh">
                <RefreshCw size={16} /> Refresh
              </button>
            </div>

            <div className="activity-timeline">
              {activityLogs.length > 0 ? (
                activityLogs.map((log, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-dot" />
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className={`action-badge ${log.category || 'general'}`}>
                          {log.action}
                        </span>
                        <span className="timeline-time">
                          {formatDate(log.createdAt)} {formatTime(log.createdAt)}
                        </span>
                      </div>
                      <p className="timeline-desc">{log.description}</p>
                      <span className="timeline-user">
                        {log.userName || log.userEmail || 'System'}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-state">No activity logs</p>
              )}
            </div>
          </div>
        )}

        {/* Mentorship Tab */}
        {activeTab === 'qoreconnect' && activeSubTab === 'mentorship' && (
          <div className="tab-mentorship">
            <div className="tab-header">
              <h2><Heart size={22} /> Mentorship Management</h2>
              <button onClick={fetchMentorshipData} className="btn-refresh" disabled={mentorshipLoading}>
                <RefreshCw size={16} className={mentorshipLoading ? 'spin' : ''} /> Refresh
              </button>
            </div>

            {/* Mentorship Stats */}
            {mentorshipStats && (
              <div className="mentorship-stats-grid">
                <div className="mentorship-stat-card">
                  <div className="stat-icon pending"><Clock size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{mentorshipStats.pendingMentors || 0}</span>
                    <span className="stat-label">Pending Mentors</span>
                  </div>
                </div>
                <div className="mentorship-stat-card">
                  <div className="stat-icon approved"><UserCheck size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{mentorshipStats.approvedMentors || 0}</span>
                    <span className="stat-label">Active Mentors</span>
                  </div>
                </div>
                <div className="mentorship-stat-card">
                  <div className="stat-icon requests"><MessageSquare size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{mentorshipStats.pendingSessions || 0}</span>
                    <span className="stat-label">Pending Sessions</span>
                  </div>
                </div>
                <div className="mentorship-stat-card">
                  <div className="stat-icon active"><Heart size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{mentorshipStats.totalSessions || 0}</span>
                    <span className="stat-label">Total Sessions</span>
                  </div>
                </div>
              </div>
            )}

            {/* Two Column Layout - Mentors & Requests */}
            <div className="mentorship-panels">
              {/* Pending Mentor Applications */}
              <section className="mentorship-section">
                <div className="section-header">
                  <h3><UserPlus size={18} /> Mentor Applications</h3>
                  <div className="filter-group">
                    <select 
                      value={mentorFilter.status} 
                      onChange={(e) => setMentorFilter(f => ({ ...f, status: e.target.value }))}
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
                
                <div className="mentors-list">
                  {mentorshipLoading ? (
                    <div className="loading-state">
                      <RefreshCw size={24} className="spin" />
                      <p>Loading mentors...</p>
                    </div>
                  ) : mentors.length > 0 ? (
                    mentors.map(mentor => (
                      <div key={mentor._id} className={`mentor-card ${mentor.status}`}>
                        <div className="mentor-header">
                          <div className="mentor-avatar">
                            {mentor.firstName?.[0] || mentor.userId?.firstName?.[0] || 'M'}{mentor.lastName?.[0] || mentor.userId?.lastName?.[0] || ''}
                          </div>
                          <div className="mentor-info">
                            <h4>{mentor.firstName || mentor.userId?.firstName} {mentor.lastName || mentor.userId?.lastName}</h4>
                            <p className="mentor-email">{mentor.email || mentor.userId?.email}</p>
                            <div className="mentor-meta">
                              <span><Briefcase size={12} /> {mentor.expertiseAreas?.join(', ') || mentor.expertise?.join(', ') || 'Not specified'}</span>
                              <span><Clock size={12} /> {mentor.yearsOfExperience || mentor.experience || 0} years exp</span>
                            </div>
                          </div>
                          <span className={`status-badge ${mentor.status}`}>
                            {mentor.status}
                          </span>
                        </div>
                        
                        {mentor.bio && (
                          <p className="mentor-bio">{mentor.bio}</p>
                        )}
                        
                        {mentor.linkedIn && (
                          <a href={mentor.linkedIn} target="_blank" rel="noopener noreferrer" className="mentor-link">
                            <Link size={12} /> LinkedIn Profile <ExternalLink size={10} />
                          </a>
                        )}
                        
                        <div className="mentor-actions">
                          {mentor.status === 'pending' && (
                            <>
                              <button 
                                className="btn-approve"
                                onClick={() => handleApproveMentor(mentor._id)}
                                disabled={processingMentor === mentor._id}
                              >
                                {processingMentor === mentor._id ? <RefreshCw size={14} className="spin" /> : <Check size={14} />}
                                Approve
                              </button>
                              <button 
                                className="btn-reject"
                                onClick={() => {
                                  setConfirmModal({
                                    isOpen: true,
                                    title: 'Reject Mentor Application',
                                    message: `Are you sure you want to reject ${mentor.firstName || mentor.userId?.firstName}'s mentor application?`,
                                    danger: true,
                                    confirmText: 'Reject',
                                    onConfirm: () => {
                                      setConfirmModal({ isOpen: false });
                                      handleRejectMentor(mentor._id, 'Application did not meet requirements');
                                    }
                                  });
                                }}
                                disabled={processingMentor === mentor._id}
                              >
                                <X size={14} /> Reject
                              </button>
                            </>
                          )}
                          {mentor.status === 'approved' && (
                            <button 
                              className="btn-suspend"
                              onClick={() => handleUpdateMentorStatus(mentor._id, 'suspended', 'Suspended by admin')}
                              disabled={processingMentor === mentor._id}
                            >
                              <Ban size={14} /> Suspend
                            </button>
                          )}
                          {mentor.status === 'suspended' && (
                            <button 
                              className="btn-reactivate"
                              onClick={() => handleUpdateMentorStatus(mentor._id, 'approved', 'Reactivated by admin')}
                              disabled={processingMentor === mentor._id}
                            >
                              <Check size={14} /> Reactivate
                            </button>
                          )}
                          <button 
                            className="btn-view"
                            onClick={() => navigate(`/qoreconnect/mentorship?mentor=${mentor._id}`)}
                          >
                            <ExternalLink size={14} /> View Profile
                          </button>
                        </div>
                        
                        <div className="mentor-footer">
                          <span className="applied-date">
                            Applied: {formatDate(mentor.createdAt)}
                          </span>
                          {mentor.updatedAt && mentor.status !== 'pending' && (
                            <span className="updated-date">
                              Updated: {formatDate(mentor.updatedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No mentor applications found</p>
                  )}
                </div>
              </section>

              {/* Mentoring Sessions */}
              <section className="mentorship-section">
                <div className="section-header">
                  <h3><MessageSquare size={18} /> Mentoring Sessions</h3>
                  <div className="filter-group">
                    <select 
                      value={sessionFilter.status} 
                      onChange={(e) => setSessionFilter(f => ({ ...f, status: e.target.value }))}
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                
                <div className="requests-list">
                  {mentorshipLoading ? (
                    <div className="loading-state">
                      <RefreshCw size={24} className="spin" />
                      <p>Loading sessions...</p>
                    </div>
                  ) : mentoringSessions.length > 0 ? (
                    mentoringSessions.map(session => (
                      <div key={session._id} className={`request-card ${session.status}`}>
                        <div className="request-header">
                          <div className="request-parties">
                            <div className="party mentee">
                              <span className="party-label">Mentee</span>
                              <span className="party-name">
                                {session.mentee?.firstName || session.mentee?.username || 'N/A'} {session.mentee?.lastName || ''}
                              </span>
                            </div>
                            <div className="arrow">→</div>
                            <div className="party mentor">
                              <span className="party-label">Mentor</span>
                              <span className="party-name">
                                {session.mentor?.firstName || session.mentor?.user?.firstName || 'N/A'} {session.mentor?.lastName || session.mentor?.user?.lastName || ''}
                              </span>
                            </div>
                          </div>
                          <span className={`status-badge ${session.status}`}>
                            {session.status}
                          </span>
                        </div>
                        
                        {session.title && (
                          <h4 className="session-title">{session.title}</h4>
                        )}
                        
                        <div className="session-details">
                          {session.scheduledDate && (
                            <span className="detail-item">
                              <Calendar size={12} /> {new Date(session.scheduledDate).toLocaleDateString()} at {new Date(session.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          {session.duration && (
                            <span className="detail-item">
                              <Clock size={12} /> {session.duration} min
                            </span>
                          )}
                          {session.sessionType && (
                            <span className="detail-item">
                              <Tag size={12} /> {session.sessionType}
                            </span>
                          )}
                          {session.format && (
                            <span className="detail-item format-badge">
                              {session.format === 'video' ? '📹' : session.format === 'audio' ? '🎧' : '💬'} {session.format}
                            </span>
                          )}
                        </div>
                        
                        {session.notes && (
                          <p className="request-message">"{session.notes}"</p>
                        )}
                        
                        <div className="request-actions">
                          {session.status === 'pending' && (
                            <>
                              <button 
                                className="btn-approve"
                                onClick={() => handleUpdateSessionStatus(session._id, 'confirmed')}
                              >
                                <Check size={14} /> Confirm
                              </button>
                              <button 
                                className="btn-reject"
                                onClick={() => handleUpdateSessionStatus(session._id, 'cancelled')}
                              >
                                <X size={14} /> Cancel
                              </button>
                            </>
                          )}
                          {session.status === 'confirmed' && (
                            <button 
                              className="btn-activate"
                              onClick={() => handleUpdateSessionStatus(session._id, 'scheduled')}
                            >
                              <Play size={14} /> Mark Scheduled
                            </button>
                          )}
                          {(session.status === 'scheduled' || session.status === 'confirmed') && (
                            <button 
                              className="btn-complete"
                              onClick={() => handleUpdateSessionStatus(session._id, 'completed')}
                            >
                              <CheckCircle size={14} /> Mark Complete
                            </button>
                          )}
                        </div>
                        
                        <div className="request-footer">
                          <span>Created: {formatDate(session.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No mentoring sessions found</p>
                  )}
                </div>
              </section>
            </div>

            {/* Quick Link to Mentorship Page */}
            <div className="mentorship-page-link">
              <button onClick={() => navigate('/qoreconnect/mentorship')} className="btn-goto-mentorship">
                <ExternalLink size={16} />
                Go to Mentorship Page
              </button>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="tab-messages">
            <div className="tab-header">
              <h2><Inbox size={22} /> Contact Messages</h2>
              <button onClick={fetchContactData} className="btn-refresh">
                <RefreshCw size={16} className={contactLoading ? 'spin' : ''} />
                Refresh
              </button>
            </div>

            {/* Message Stats */}
            {contactStats && (
              <div className="message-stats-grid">
                <div className="message-stat-card new">
                  <div className="stat-icon"><Mail size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{contactStats.newMessages || 0}</span>
                    <span className="stat-label">New Messages</span>
                  </div>
                </div>
                <div className="message-stat-card progress">
                  <div className="stat-icon"><Clock size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{contactStats.inProgress || 0}</span>
                    <span className="stat-label">In Progress</span>
                  </div>
                </div>
                <div className="message-stat-card resolved">
                  <div className="stat-icon"><CheckCircle size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{contactStats.resolved || 0}</span>
                    <span className="stat-label">Resolved</span>
                  </div>
                </div>
                <div className="message-stat-card week">
                  <div className="stat-icon"><Calendar size={24} /></div>
                  <div className="stat-info">
                    <span className="stat-value">{contactStats.thisWeek || 0}</span>
                    <span className="stat-label">This Week</span>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="message-filters">
              <div className="filter-group">
                <select 
                  value={contactFilter.status} 
                  onChange={(e) => setContactFilter(f => ({ ...f, status: e.target.value }))}
                >
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="in-progress">In Progress</option>
                  <option value="responded">Responded</option>
                  <option value="resolved">Resolved</option>
                  <option value="archived">Archived</option>
                </select>
                <select 
                  value={contactFilter.inquiryType} 
                  onChange={(e) => setContactFilter(f => ({ ...f, inquiryType: e.target.value }))}
                >
                  <option value="">All Types</option>
                  <option value="general">General</option>
                  <option value="consulting">Consulting</option>
                  <option value="partnership">Partnership</option>
                  <option value="speaking">Speaking</option>
                  <option value="training">Training</option>
                  <option value="support">Support</option>
                  <option value="media">Media</option>
                  <option value="careers">Careers</option>
                </select>
                <div className="search-input">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search messages..."
                    value={contactFilter.search}
                    onChange={(e) => setContactFilter(f => ({ ...f, search: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Messages Layout */}
            <div className="messages-layout">
              {/* Message List */}
              <div className="messages-list">
                {contactLoading ? (
                  <div className="loading-state">
                    <RefreshCw size={24} className="spin" />
                    <p>Loading messages...</p>
                  </div>
                ) : contactMessages.length > 0 ? (
                  contactMessages.map(msg => (
                    <div 
                      key={msg._id} 
                      className={`message-item ${msg.status} ${selectedMessage?._id === msg._id ? 'selected' : ''}`}
                      onClick={() => setSelectedMessage(msg)}
                    >
                      <div className="message-header">
                        <div className="sender-info">
                          <span className="sender-name">{msg.name}</span>
                          <span className="sender-email">{msg.email}</span>
                        </div>
                        <span className={`status-badge ${msg.status}`}>{msg.status}</span>
                      </div>
                      <h4 className="message-subject">{msg.subject}</h4>
                      <p className="message-preview">{msg.message?.substring(0, 100)}...</p>
                      <div className="message-meta">
                        <span className={`inquiry-type ${msg.inquiryType}`}>{msg.inquiryType}</span>
                        <span className="message-date">{formatDate(msg.createdAt)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Inbox size={48} />
                    <p>No messages found</p>
                  </div>
                )}
              </div>

              {/* Message Detail */}
              {selectedMessage && (
                <div className="message-detail">
                  <div className="detail-header">
                    <div className="detail-sender">
                      <h3>{selectedMessage.name}</h3>
                      <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                      {selectedMessage.phone && <span className="phone">{selectedMessage.phone}</span>}
                      {selectedMessage.company && <span className="company"><Briefcase size={14} /> {selectedMessage.company}</span>}
                    </div>
                    <button className="btn-close" onClick={() => setSelectedMessage(null)}>
                      <X size={18} />
                    </button>
                  </div>

                  <div className="detail-meta">
                    <span className={`inquiry-type ${selectedMessage.inquiryType}`}>{selectedMessage.inquiryType}</span>
                    <span className={`status-badge ${selectedMessage.status}`}>{selectedMessage.status}</span>
                    <span className="date">{formatDate(selectedMessage.createdAt)}</span>
                  </div>

                  <h4 className="detail-subject">{selectedMessage.subject}</h4>
                  
                  <div className="detail-message">
                    <p>{selectedMessage.message}</p>
                  </div>

                  {(selectedMessage.budget || selectedMessage.timeline) && (
                    <div className="detail-extras">
                      {selectedMessage.budget && (
                        <div className="extra-item">
                          <span className="label">Budget:</span>
                          <span className="value">{selectedMessage.budget}</span>
                        </div>
                      )}
                      {selectedMessage.timeline && (
                        <div className="extra-item">
                          <span className="label">Timeline:</span>
                          <span className="value">{selectedMessage.timeline}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Response Section */}
                  {selectedMessage.responseMessage ? (
                    <div className="previous-response">
                      <h5><Reply size={14} /> Previous Response</h5>
                      <p>{selectedMessage.responseMessage}</p>
                      <small>Responded: {formatDate(selectedMessage.respondedAt)}</small>
                    </div>
                  ) : (
                    <div className="reply-section">
                      <h5><Reply size={14} /> Reply to Message</h5>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your response..."
                        rows={4}
                      />
                      <button 
                        className="btn-send-reply"
                        onClick={() => handleReplyToMessage(selectedMessage._id)}
                        disabled={!replyText.trim()}
                      >
                        <Send size={14} /> Send Response
                      </button>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="detail-actions">
                    <select 
                      value={selectedMessage.status}
                      onChange={(e) => handleUpdateMessageStatus(selectedMessage._id, e.target.value)}
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="in-progress">In Progress</option>
                      <option value="responded">Responded</option>
                      <option value="resolved">Resolved</option>
                    </select>
                    <button 
                      className="btn-archive"
                      onClick={() => handleArchiveMessage(selectedMessage._id)}
                    >
                      <Archive size={14} /> Archive
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => {
                        setConfirmModal({
                          isOpen: true,
                          title: 'Delete Message',
                          message: 'Are you sure you want to delete this message? This action cannot be undone.',
                          danger: true,
                          onConfirm: () => {
                            handleDeleteMessage(selectedMessage._id);
                            setConfirmModal({ isOpen: false });
                          }
                        });
                      }}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'qoretalks' && activeSubTab === 'guests' && (
          <QoreTalksGuestsPanel
            token={token}
            showNotification={showNotification}
            setConfirmModal={setConfirmModal}
          />
        )}

        {activeTab === 'analytics' && (
          <AdminAnalyticsPanel
            token={token}
            activeSubTab={activeSubTab || 'overview'}
            onNotify={showNotification}
          />
        )}

        {/* Financial Management - Overview */}
        {activeTab === 'financial' && activeSubTab === 'overview' && (
          <div className="tab-financial">
            <div className="tab-header">
              <h2>Financial Overview</h2>
              <div className="financial-controls">
                <select value={financialPeriod} onChange={(e) => setFinancialPeriod(e.target.value)}>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="12m">Last 12 Months</option>
                  <option value="ytd">Year to Date</option>
                </select>
              </div>
            </div>

            {financialData && !financialLoading ? (
              <div className="financial-content">
                {/* Revenue Cards */}
                <div className="financial-cards">
                  <div className="financial-card primary">
                    <div className="card-icon"><DollarSign size={24} /></div>
                    <div className="card-content">
                      <span className="card-label">Total Revenue</span>
                      <span className="card-value">${financialData.revenue.total.toLocaleString()}</span>
                      <div className="card-trend positive">
                        <TrendingUp size={14} />
                        <span>+{financialData.revenue.growth}% vs last period</span>
                      </div>
                    </div>
                  </div>

                  <div className="financial-card success">
                    <div className="card-icon"><Wallet size={24} /></div>
                    <div className="card-content">
                      <span className="card-label">MRR</span>
                      <span className="card-value">${financialData.revenue.mrr.toLocaleString()}</span>
                      <small>Monthly Recurring Revenue</small>
                    </div>
                  </div>

                  <div className="financial-card info">
                    <div className="card-icon"><TrendingUp size={24} /></div>
                    <div className="card-content">
                      <span className="card-label">ARR</span>
                      <span className="card-value">${financialData.revenue.arr.toLocaleString()}</span>
                      <small>Annual Recurring Revenue</small>
                    </div>
                  </div>

                  <div className="financial-card warning">
                    <div className="card-icon"><Users size={24} /></div>
                    <div className="card-content">
                      <span className="card-label">ARPU</span>
                      <span className="card-value">${financialData.metrics.avgRevenuePerUser}</span>
                      <small>Avg Revenue Per User</small>
                    </div>
                  </div>
                </div>

                {/* Key Financial Metrics */}
                <section className="financial-section">
                  <h3><PieChart size={18} /> Key Metrics</h3>
                  <div className="metrics-grid">
                    <div className="metric-item">
                      <span className="metric-label">Customer Lifetime Value</span>
                      <span className="metric-value">${financialData.metrics.lifetimeValue}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Payment Success Rate</span>
                      <span className="metric-value">{financialData.metrics.paymentSuccessRate}%</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Active Subscriptions</span>
                      <span className="metric-value">{financialData.subscriptions.active}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Churn Rate</span>
                      <span className="metric-value">{financialData.subscriptions.churnRate}%</span>
                    </div>
                  </div>
                </section>

                {/* Recent Transactions */}
                <section className="financial-section">
                  <h3><Receipt size={18} /> Recent Transactions</h3>
                  <div className="transactions-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Transaction ID</th>
                          <th>Date</th>
                          <th>Customer</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financialData.transactions.slice(0, 10).map(txn => (
                          <tr key={txn.id}>
                            <td><code>{txn.id}</code></td>
                            <td>{new Date(txn.date).toLocaleDateString()}</td>
                            <td>{txn.customer}</td>
                            <td><span className="badge">{txn.type}</span></td>
                            <td className="amount">${txn.amount}</td>
                            <td>
                              <span className={`status ${txn.status}`}>
                                {txn.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            ) : (
              <p className="empty-state">Loading financial data...</p>
            )}
          </div>
        )}

        {/* Financial - Revenue Sub-tab */}
        {activeTab === 'financial' && activeSubTab === 'revenue' && (
          <div className="tab-placeholder">
            <TrendingUp size={48} style={{ opacity: 0.5 }} />
            <h2>Revenue Analytics</h2>
            <p>Revenue trends, forecasting, growth analysis, and revenue breakdown by source. Coming soon.</p>
          </div>
        )}

        {/* Financial - Transactions Sub-tab */}
        {activeTab === 'financial' && activeSubTab === 'transactions' && (
          <div className="tab-placeholder">
            <Receipt size={48} style={{ opacity: 0.5 }} />
            <h2>Transaction Management</h2>
            <p>View all transactions, process refunds, manage disputes, and transaction history. Coming soon.</p>
          </div>
        )}

        {/* Financial - Subscriptions Sub-tab */}
        {activeTab === 'financial' && activeSubTab === 'subscriptions' && (
          <div className="tab-placeholder">
            <CreditCard size={48} style={{ opacity: 0.5 }} />
            <h2>Subscription Management</h2>
            <p>Active subscriptions, trials, cancellations, upgrades/downgrades, and churn analysis. Coming soon.</p>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <AdminSettingsPanel token={token} onNotify={showNotification} />
        )}

        {/* QoreTalks Sub-Tabs */}
        {activeTab === 'qoretalks' && activeSubTab === 'episodes' && (
          <QoreTalksEpisodesPanel token={token} />
        )}

        {activeTab === 'qoretalks' && activeSubTab === 'livestream' && (
          <QoreTalksLivestreamPanel token={token} onNotify={showNotification} />
        )}

        {activeTab === 'qoretalks' && activeSubTab === 'subscribers' && (
          <QoreTalksSubscribersPanel token={token} />
        )}

        {activeTab === 'qoreconnect' && activeSubTab === 'community' && (
          <QoreConnectCommunityPanel token={token} onNotify={showNotification} />
        )}

        {activeTab === 'qoreconnect' && activeSubTab === 'forum' && (
          <QoreConnectForumPanel token={token} onNotify={showNotification} />
        )}

        {activeTab === 'qoreconnect' && activeSubTab === 'jobs' && (
          <QoreConnectJobsPanel token={token} onNotify={showNotification} />
        )}

        {activeTab === 'qoreacademy' && activeSubTab === 'labs' && (
          <AcademyLabsPanel token={token} onNotify={showNotification} />
        )}
        {activeTab === 'qoreacademy' && activeSubTab === 'certificates' && (
          <AcademyCertificatesPanel token={token} onNotify={showNotification} />
        )}
        {activeTab === 'qoreacademy' && activeSubTab === 'career' && (
          <AcademyCareerPanel />
        )}
        {activeTab === 'qoreacademy' && activeSubTab === 'tutor' && (
          <AcademyTutorPanel token={token} onNotify={showNotification} />
        )}

        {activeTab === 'financial' && (
          <FinancePanel token={token} onNotify={showNotification} />
        )}

        {activeTab === 'support' && activeSubTab === 'tickets' && (
          <SupportTicketsPanel token={token} onNotify={showNotification} />
        )}
        {activeTab === 'support' && activeSubTab === 'templates' && (
          <EmailTemplatesPanel token={token} onNotify={showNotification} />
        )}

        {activeTab === 'ai-orchestrator' && activeSubTab === 'pulse' && (
          <div className="qt-admin tab-ai-agent">
            <div className="agent-header nova-v3">
              <div className="agent-identity">
                <div className="agent-avatar nova">
                  <Radio size={32} />
                </div>
                <div className="agent-info">
                  <h2>Pulse <span className="agent-badge">QoreTalks</span></h2>
                  <p>Editorial coworker — guests, episodes, live rooms, subscribers.</p>
                </div>
              </div>
            </div>
            <div className="atlas-actions-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', padding: '1rem 0' }}>
              <button type="button" className="atlas-action-card" onClick={() => navigateTo('qoretalks', 'guests')}>Review guests</button>
              <button type="button" className="atlas-action-card" onClick={() => navigateTo('qoretalks', 'episodes')}>Episodes</button>
              <button type="button" className="atlas-action-card" onClick={() => runCommandTool('propose_cross_line', {}, 'Cross-line')}>Guest → mentor</button>
            </div>
          </div>
        )}

        {/* AI Agent Tab - NOVA v3.0 */}
        {activeTab === 'ai-orchestrator' && activeSubTab === 'nova' && (
          <div className="tab-ai-agent">
            {/* Agent Header with Autonomous Toggle */}
            <div className="agent-header nova-v3">
              <div className="agent-identity">
                <div className="agent-avatar nova">
                  <Bot size={32} />
                  <span className="agent-pulse" />
                  <Crown size={14} className="crown-icon" />
                </div>
                <div className="agent-info">
                  <h2>Nova <span className="agent-badge nova">v4.0</span> <span className="agent-badge orchestrator">QoreNova Coworker</span></h2>
                  <p>
                    {autonomousMode 
                      ? `Autonomous Mode • ${Math.round((agentStatus?.autonomyLevel || 0.95) * 100)}% autonomy • ${subAgents.length} sub-agents active`
                      : `Supervised Mode • 50% autonomy • Manual approval required`
                    }
                  </p>
                </div>
              </div>
              <div className="agent-controls">
                <div className="autonomous-toggle">
                  <span className="toggle-label">Autonomous</span>
                  <button 
                    className={`toggle-switch ${autonomousMode ? 'active' : ''}`}
                    onClick={toggleAutonomousMode}
                    title={autonomousMode ? 'Switch to supervised mode' : 'Enable autonomous mode'}
                  >
                    <span className="toggle-slider" />
                    <Power size={12} className="toggle-icon" />
                  </button>
                </div>
                <div className={`status-indicator ${agentStatus?.status || 'active'}`}>
                  <Radio size={14} />
                  <span>{agentStatus?.status || 'Active'}</span>
                </div>
              </div>
            </div>

            {/* Sub-Agents Grid */}
            <section className="sub-agents-section">
              <h3><Network size={18} /> Sub-Agent Orchestra</h3>
              <div className="sub-agents-grid">
                {subAgents.map(agent => (
                  <div key={agent.id} className={`sub-agent-card ${agent.status}`}>
                    <div className="sub-agent-header">
                      <div className="sub-agent-identity">
                        {agent.id === 'userAgent' && <UserCog size={20} />}
                        {agent.id === 'contentAgent' && <FileText size={20} />}
                        {agent.id === 'communityAgent' && <Shield size={20} />}
                        {agent.id === 'analyticsAgent' && <BarChart3 size={20} />}
                        {agent.id === 'techAgent' && <Terminal size={20} />}
                        {agent.id === 'instructorAgent' && <GraduationCap size={20} />}
                        {agent.id === 'revenueAgent' && <DollarSign size={20} />}
                        {agent.id === 'supportAgent' && <MessageSquare size={20} />}
                        {agent.id === 'talksAgent' && <Radio size={20} />}
                        <div>
                          <h4>{agent.name}</h4>
                          <span className="sub-agent-domain">{agent.domain}</span>
                        </div>
                      </div>
                      <span className={`status-dot ${agent.status}`} />
                    </div>
                    <div className="sub-agent-metrics">
                      <div><span>{agent.metrics?.tasksCompleted || 0}</span> tasks</div>
                      <div><span>{agent.metrics?.issuesFound || 0}</span> issues</div>
                    </div>
                    <div className="sub-agent-capabilities">
                      {agent.capabilities?.slice(0, 3).map((cap, i) => (
                        <span key={i} className="capability-tag">{cap.replace(/_/g, ' ')}</span>
                      ))}
                    </div>
                    {agent.lastRun && (
                      <div className="sub-agent-last-run">
                        <Clock size={12} /> {new Date(agent.lastRun).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Agent Capabilities Grid */}
            <section className="agent-capabilities">
              <h3><Cpu size={18} /> Active Capabilities</h3>
              <div className="capabilities-grid">
                {(agentStatus?.capabilities || [
                  { id: 'health_monitoring', name: 'Health Monitoring', status: 'active' },
                  { id: 'user_analytics', name: 'User Analytics', status: 'active' },
                  { id: 'content_review', name: 'Content Review', status: 'active' },
                  { id: 'auto_moderation', name: 'Auto Moderation', status: 'active' },
                  { id: 'predictive_insights', name: 'Predictive Insights', status: 'active' },
                  { id: 'anomaly_detection', name: 'Anomaly Detection', status: 'active' },
                  { id: 'auto_optimization', name: 'Auto-Optimization', status: 'active' },
                  { id: 'report_generation', name: 'Report Generation', status: 'active' }
                ]).map(cap => (
                  <div key={cap.id} className={`capability-card ${cap.status}`}>
                    <CheckCircle size={16} />
                    <span>{cap.name}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Platform Health from Agent */}
            <section className="agent-health-section">
              <div className="section-header-row">
                <h3><Activity size={18} /> Platform Health</h3>
                <button onClick={fetchAgentData} className="btn-refresh" disabled={agentLoading}>
                  <RefreshCw size={16} className={agentLoading ? 'spin' : ''} />
                </button>
              </div>
              {agentHealth ? (
                <div className="health-overview">
                  <div className={`health-score-card ${agentHealth.status}`}>
                    <div className="health-score">{agentHealth.score}</div>
                    <div className="health-label">Health Score</div>
                    <div className={`health-status ${agentHealth.status}`}>{agentHealth.status}</div>
                  </div>
                  <div className="health-metrics">
                    <div className="metric">
                      <Users size={16} />
                      <div>
                        <span className="metric-value">{agentHealth.metrics?.users?.total || 0}</span>
                        <span className="metric-label">Total Users</span>
                      </div>
                    </div>
                    <div className="metric">
                      <Activity size={16} />
                      <div>
                        <span className="metric-value">{agentHealth.metrics?.users?.active24h || 0}</span>
                        <span className="metric-label">Active (24h)</span>
                      </div>
                    </div>
                    <div className="metric">
                      <BookOpen size={16} />
                      <div>
                        <span className="metric-value">{agentHealth.metrics?.courses?.published || 0}</span>
                        <span className="metric-label">Courses</span>
                      </div>
                    </div>
                    <div className="metric">
                      <GraduationCap size={16} />
                      <div>
                        <span className="metric-value">{agentHealth.metrics?.enrollments?.total || 0}</span>
                        <span className="metric-label">Enrollments</span>
                      </div>
                    </div>
                    <div className="metric">
                      <Radio size={16} />
                      <div>
                        <span className="metric-value">{agentHealth.metrics?.talks?.published || 0}</span>
                        <span className="metric-label">Talks live</span>
                      </div>
                    </div>
                    <div className="metric">
                      <Users size={16} />
                      <div>
                        <span className="metric-value">{agentHealth.metrics?.connect?.forumActive || 0}</span>
                        <span className="metric-label">Connect posts</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="loading-placeholder">Loading health data...</div>
              )}
            </section>

            {/* Scan & Insights Row */}
            <div className="agent-actions-row">
              {/* Platform Scan */}
              <section className="agent-scan-section">
                <div className="section-header-row">
                  <h3><Target size={18} /> Platform Scan</h3>
                  <button onClick={runAgentScan} className="btn-scan" disabled={scanRunning}>
                    {scanRunning ? <RefreshCw size={16} className="spin" /> : <Play size={16} />}
                    <span>{scanRunning ? 'Scanning...' : 'Run Scan'}</span>
                  </button>
                </div>
                {agentScan ? (
                  <div className="scan-results">
                    <div className="scan-summary">
                      <div className={`scan-status ${agentScan.summary?.overallStatus || 'healthy'}`}>
                        {agentScan.summary?.overallStatus === 'attention_needed' ? (
                          <AlertCircle size={20} />
                        ) : (
                          <CheckCircle size={20} />
                        )}
                        <span>{agentScan.summary?.overallStatus === 'attention_needed' ? 'Attention Needed' : 'All Clear'}</span>
                      </div>
                      <div className="scan-counts">
                        <span>{agentScan.summary?.issueCount || 0} issues</span>
                        <span>{agentScan.summary?.recommendationCount || 0} recommendations</span>
                      </div>
                    </div>
                    {agentScan.recommendations?.length > 0 && (
                      <div className="recommendations-list">
                        {agentScan.recommendations.slice(0, 4).map((rec, i) => (
                          <div key={i} className={`recommendation-item ${rec.priority}`}>
                            <div className="rec-header">
                              <span className={`priority-badge ${rec.priority}`}>{rec.priority}</span>
                              <span className="rec-type">{rec.type.replace(/_/g, ' ')}</span>
                            </div>
                            <p>{rec.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="scan-placeholder">
                    <Terminal size={24} />
                    <p>Run a platform scan to detect issues and get recommendations</p>
                  </div>
                )}
              </section>

              {/* AI Insights */}
              <section className="agent-insights-section">
                <div className="section-header-row">
                  <h3><Brain size={18} /> AI Insights</h3>
                  <button onClick={() => fetchAgentInsights('7d')} className="btn-refresh" disabled={agentLoading}>
                    <Zap size={16} />
                    <span>Generate</span>
                  </button>
                </div>
                {agentInsights ? (
                  <div className="insights-content">
                    {agentInsights.insights?.observations && (
                      <div className="insight-block">
                        <h4>Key Observations</h4>
                        <p>{typeof agentInsights.insights.observations === 'string' 
                          ? agentInsights.insights.observations 
                          : JSON.stringify(agentInsights.insights.observations)}</p>
                      </div>
                    )}
                    {agentInsights.insights?.immediateActions && (
                      <div className="insight-block">
                        <h4>Recommended Actions</h4>
                        <ul>
                          {(Array.isArray(agentInsights.insights.immediateActions) 
                            ? agentInsights.insights.immediateActions 
                            : [agentInsights.insights.immediateActions]
                          ).slice(0, 3).map((action, i) => (
                            <li key={i}>{typeof action === 'string' ? action : JSON.stringify(action)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {agentInsights.insights?.rawAnalysis && (
                      <div className="insight-block">
                        <p className="raw-analysis">{agentInsights.insights.rawAnalysis.substring(0, 500)}...</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="insights-placeholder">
                    <Sparkles size={24} />
                    <p>Generate AI-powered insights about your platform</p>
                  </div>
                )}
              </section>
            </div>

            {/* Chat with Agent */}
            <section className="agent-chat-section">
              <div className="agent-chat-header">
                <h3><MessageSquare size={18} /> Chat with Nova</h3>
                {agentChat.length > 0 && (
                  <button className="btn-clear-chat" onClick={() => setAgentChat([])} title="Clear conversation">
                    <Trash2 size={15} /> Clear
                  </button>
                )}
              </div>
              <div className="agent-chat-container">
                <div className="chat-messages">
                  {agentChat.length === 0 && (
                    <div className="chat-empty-state">
                      <div className="chat-empty-avatar">
                        <Bot size={36} />
                        <span className="nova-pulse" />
                      </div>
                      <h4>Ask Nova to do the work…</h4>
                      <p>Academy, Talks, and Connect. I can approve, moderate, and run cross-line plays — not just summarize.</p>
                      <div className="suggested-prompts">
                        <button onClick={() => setChatInput('Give me the three-line QoreNova command brief.')}>
                          <Activity size={13} /> Three-line brief
                        </button>
                        <button onClick={() => setChatInput('Clear the action inbox — tell me what you can execute now.')}>
                          <Inbox size={13} /> Action inbox
                        </button>
                        <button onClick={() => setChatInput('Which QoreTalks guests should become QoreConnect mentors?')}>
                          <Users size={13} /> Guest → mentor
                        </button>
                        <button onClick={() => setChatInput('What is at risk on QoreAcademy this week?')}>
                          <BarChart3 size={13} /> Academy risks
                        </button>
                      </div>
                    </div>
                  )}
                  {agentChat.map((msg, i) => (
                    <div key={i} className={`chat-bubble-wrap ${msg.role}`}>
                      {msg.role === 'agent' && (
                        <div className="chat-avatar nova-avatar" title="Nova"><Bot size={16} /></div>
                      )}
                      <div className={`chat-bubble ${msg.role}`}>
                        <p className="bubble-text">{msg.content}</p>
                        {msg.tools?.length > 0 && (
                          <ul className="bubble-tools">
                            {msg.tools.map((t, idx) => (
                              <li key={idx}>{t.name}: {t.result?.message || t.result?.status || 'done'}</li>
                            ))}
                          </ul>
                        )}
                        <span className="bubble-time">
                          {new Date(msg.timestamp).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}
                        </span>
                      </div>
                      {msg.role === 'user' && (
                        <div className="chat-avatar user-avatar" title="You">
                          {(user?.firstName || 'A').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  ))}
                  {agentLoading && (
                    <div className="chat-bubble-wrap agent">
                      <div className="chat-avatar nova-avatar"><Bot size={16} /></div>
                      <div className="chat-bubble agent typing-bubble">
                        <div className="typing-dots"><span /><span /><span /></div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="chat-input-container">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendAgentMessage()}
                    placeholder="Ask Nova anything about the platform…"
                    disabled={agentLoading}
                  />
                  <button
                    className={`btn-chat-send ${chatInput.trim() ? 'active' : ''}`}
                    onClick={sendAgentMessage}
                    disabled={agentLoading || !chatInput.trim()}
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </section>

            {/* Recent Agent Actions */}
            <section className="agent-actions-log">
              <h3><Clock size={18} /> Recent Agent Actions</h3>
              <div className="actions-list">
                {(agentStatus?.recentActions || []).length > 0 ? (
                  agentStatus.recentActions.map((action, i) => (
                    <div key={i} className="action-log-item">
                      <div className="action-type">{action.type}</div>
                      <div className="action-input">{typeof action.input === 'string' ? action.input : JSON.stringify(action.input)}</div>
                      <div className="action-time">{new Date(action.timestamp).toLocaleString()}</div>
                    </div>
                  ))
                ) : (
                  <p className="empty-state">No recent actions logged</p>
                )}
              </div>
            </section>
          </div>
        )}

        {/* Atlas Sub-Agent - User Engagement */}
        {activeTab === 'ai-orchestrator' && activeSubTab === 'atlas' && (
          <div className="tab-atlas-engagement">
            {/* Atlas Header */}
            <div className="agent-header atlas-header">
              <div className="agent-identity">
                <div className="agent-avatar atlas">
                  <Heart size={32} />
                  <span className="agent-pulse" />
                </div>
                <div className="agent-info">
                  <h2>Atlas <span className="agent-badge atlas">v1.0</span> <span className="agent-badge">User Engagement</span></h2>
                  <p>Automated onboarding, engagement campaigns, churn prevention & retention strategies</p>
                </div>
              </div>
              <div className="atlas-quick-actions">
                <button 
                  className="action-btn primary"
                  onClick={() => executeAtlasAction('atlas_full_engagement_run')}
                  disabled={atlasActionRunning}
                >
                  {atlasActionRunning === 'atlas_full_engagement_run' ? (
                    <><RefreshCw size={16} className="spin" /> Running...</>
                  ) : (
                    <><Zap size={16} /> Run Full Scan</>
                  )}
                </button>
                <button 
                  className="action-btn"
                  onClick={() => fetchAtlasData()}
                  disabled={atlasLoading}
                >
                  <RefreshCw size={16} className={atlasLoading ? 'spin' : ''} /> Refresh
                </button>
              </div>
            </div>

            {atlasLoading ? (
              <div className="loading-state">
                <RefreshCw size={32} className="spin" />
                <p>Loading Atlas data...</p>
              </div>
            ) : (
              <>
                {/* Engagement Metrics Grid */}
                <section className="atlas-metrics">
                  <h3><BarChart3 size={18} /> Engagement Overview</h3>
                  <div className="metrics-grid atlas-grid">
                    <div className="metric-card">
                      <div className="metric-icon engagement"><Activity size={24} /></div>
                      <div className="metric-content">
                        <span className="metric-value">{atlasData?.engagement?.high || 0}</span>
                        <span className="metric-label">Highly Engaged</span>
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-icon medium"><Users size={24} /></div>
                      <div className="metric-content">
                        <span className="metric-value">{atlasData?.engagement?.medium || 0}</span>
                        <span className="metric-label">Medium Engagement</span>
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-icon warning"><AlertTriangle size={24} /></div>
                      <div className="metric-content">
                        <span className="metric-value">{(atlasData?.churnRisk?.high || 0) + (atlasData?.churnRisk?.critical || 0)}</span>
                        <span className="metric-label">At Risk</span>
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-icon new-users"><UserPlus size={24} /></div>
                      <div className="metric-content">
                        <span className="metric-value">{atlasData?.segments?.new_user || 0}</span>
                        <span className="metric-label">New Users</span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* User Segments */}
                <section className="atlas-segments">
                  <h3><PieChart size={18} /> User Segments</h3>
                  <div className="segments-grid">
                    {Object.entries(atlasData?.segments || {}).map(([segment, count]) => (
                      <div key={segment} className={`segment-card ${segment}`}>
                        <div className="segment-count">{count}</div>
                        <div className="segment-name">{segment.replace(/_/g, ' ')}</div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Atlas Actions */}
                <section className="atlas-actions-panel">
                  <h3><Zap size={18} /> Quick Actions</h3>
                  <div className="actions-grid">
                    <button 
                      className="atlas-action-card"
                      onClick={() => executeAtlasAction('atlas_process_welcomes')}
                      disabled={atlasActionRunning}
                    >
                      <Mail size={24} />
                      <span>Process Welcome Sequences</span>
                      <small>Send onboarding emails to new users</small>
                    </button>
                    <button 
                      className="atlas-action-card"
                      onClick={() => executeAtlasAction('atlas_process_nudges')}
                      disabled={atlasActionRunning}
                    >
                      <Bell size={24} />
                      <span>Send Progress Nudges</span>
                      <small>Remind users with stalled courses</small>
                    </button>
                    <button 
                      className="atlas-action-card"
                      onClick={() => executeAtlasAction('atlas_segment_users')}
                      disabled={atlasActionRunning}
                    >
                      <Users size={24} />
                      <span>Segment Users</span>
                      <small>Categorize users by behavior</small>
                    </button>
                    <button 
                      className="atlas-action-card"
                      onClick={() => executeAtlasAction('atlas_churn_detection')}
                      disabled={atlasActionRunning}
                    >
                      <Shield size={24} />
                      <span>Detect Churn Risk</span>
                      <small>Identify at-risk users</small>
                    </button>
                    <button 
                      className="atlas-action-card"
                      onClick={() => executeAtlasAction('atlas_engagement_campaign', { campaignType: 're_engagement' })}
                      disabled={atlasActionRunning}
                    >
                      <Target size={24} />
                      <span>Re-engagement Campaign</span>
                      <small>Email inactive users</small>
                    </button>
                  </div>
                </section>

                {/* At Risk Users */}
                <section className="atlas-at-risk">
                  <h3><AlertTriangle size={18} /> At-Risk Users ({atRiskUsers.length})</h3>
                  <div className="at-risk-list">
                    {atRiskUsers.length > 0 ? (
                      atRiskUsers.slice(0, 10).map((user, i) => (
                        <div key={i} className={`at-risk-item risk-${user.churnRisk?.level || 'unknown'}`}>
                          <div className="user-info">
                            <strong>{user.name}</strong>
                            <span>{user.email}</span>
                          </div>
                          <div className="risk-info">
                            <span className={`risk-badge ${user.churnRisk?.level}`}>
                              {user.churnRisk?.level?.toUpperCase()} - Score: {user.churnRisk?.score}
                            </span>
                            <small>Last login: {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}</small>
                          </div>
                          <div className="risk-factors">
                            {(user.churnRisk?.riskFactors || []).slice(0, 2).map((f, j) => (
                              <span key={j} className="factor-tag">{f.factor?.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="empty-state">No high-risk users detected. Great job! 🎉</p>
                    )}
                  </div>
                </section>

                {/* Atlas Activity Log */}
                <section className="atlas-activity-log">
                  <h3><Clock size={18} /> Recent Atlas Activity</h3>
                  <div className="activity-list">
                    {atlasLogs.length > 0 ? (
                      atlasLogs.map((log, i) => (
                        <div key={i} className="activity-item">
                          <div className="activity-type">{log.type?.replace('atlas_', '')}</div>
                          <div className="activity-desc">{log.input}</div>
                          <div className="activity-time">{new Date(log.timestamp).toLocaleString()}</div>
                        </div>
                      ))
                    ) : (
                      <p className="empty-state">No recent Atlas activity</p>
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        )}

      {/* Course View Modal */}
      <CourseViewModal
        course={viewCourse}
        isOpen={!!viewCourse}
        onClose={() => setViewCourse(null)}
      />

      {/* Course Edit Modal */}
      <CourseEditModal
        course={editCourse}
        isOpen={!!editCourse}
        onClose={() => setEditCourse(null)}
        onSave={handleSaveCourse}
        saving={savingCourse}
      />

      {/* Path Detail Modal */}
      {pathDetailModal && selectedPath && (
        <div className="modal-overlay" onClick={() => setPathDetailModal(false)}>
          <div className="modal-content-v2 path-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Brain size={24} /> Learning Path Details
              </h2>
              <button className="btn-close" onClick={() => setPathDetailModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="path-detail-grid">
                <div className="detail-section">
                  <h4>Target Role</h4>
                  <p className="highlight">{selectedPath.targetRole}</p>
                </div>
                
                <div className="detail-section">
                  <h4>User Information</h4>
                  <div className="user-info">
                    <div className="user-avatar large">
                      {selectedPath.user.firstName.charAt(0)}
                    </div>
                    <div>
                      <p><strong>{selectedPath.user.firstName} {selectedPath.user.lastName}</strong></p>
                      <p className="text-muted">{selectedPath.user.email}</p>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Progress Overview</h4>
                  <div className="progress-stats">
                    <div className="stat-item">
                      <span className="label">Total Skills:</span>
                      <span className="value">{selectedPath.totalSkills}</span>
                    </div>
                    <div className="stat-item">
                      <span className="label">Completed:</span>
                      <span className="value">{selectedPath.completedSkills}</span>
                    </div>
                    <div className="stat-item">
                      <span className="label">Progress:</span>
                      <span className="value">
                        {Math.round((selectedPath.completedSkills / selectedPath.totalSkills) * 100)}%
                      </span>
                    </div>
                  </div>
                  <div className="progress-bar-large">
                    <div 
                      className="progress-fill"
                      style={{ width: `${(selectedPath.completedSkills / selectedPath.totalSkills) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Time Estimates</h4>
                  <div className="time-info">
                    <div className="time-item">
                      <Clock size={16} />
                      <span>Total Hours: <strong>{selectedPath.totalHours}h</strong></span>
                    </div>
                    <div className="time-item">
                      <Calendar size={16} />
                      <span>Est. Completion: <strong>{formatDate(selectedPath.estimatedCompletion)}</strong></span>
                    </div>
                    <div className="time-item">
                      <Zap size={16} />
                      <span>Generation Time: <strong>{selectedPath.generationTime}</strong></span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Status</h4>
                  <span className={`status-badge-v2 large ${selectedPath.status}`}>
                    {selectedPath.status}
                  </span>
                </div>

                <div className="detail-section full-width">
                  <h4>Timeline</h4>
                  <div className="timeline-info">
                    <div className="timeline-item">
                      <Calendar size={14} />
                      <span>Created: {formatDate(selectedPath.createdAt)}</span>
                    </div>
                    {selectedPath.startedAt && (
                      <div className="timeline-item">
                        <Play size={14} />
                        <span>Started: {formatDate(selectedPath.startedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setPathDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Limits Modal */}
      {editLimitsModal && selectedUserForLimits && (
        <div className="modal-overlay" onClick={() => setEditLimitsModal(false)}>
          <div className="modal-content-v2" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Settings size={24} /> Edit Generation Limits
              </h2>
              <button className="btn-close" onClick={() => setEditLimitsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="user-info-header">
                <div className="user-avatar large">
                  {selectedUserForLimits.name.charAt(0)}
                </div>
                <div>
                  <h4>{selectedUserForLimits.name}</h4>
                  <p className="text-muted">{selectedUserForLimits.email}</p>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>
                    <Calendar size={16} /> Daily Limit
                  </label>
                  <input 
                    type="number" 
                    min="1"
                    max="100"
                    value={customLimits.daily}
                    onChange={e => setCustomLimits(prev => ({ ...prev, daily: parseInt(e.target.value) || 1 }))}
                    disabled={customLimits.unlimited}
                  />
                  <span className="input-hint">Max paths per day</span>
                </div>

                <div className="form-group">
                  <label>
                    <CalendarIcon size={16} /> Monthly Limit
                  </label>
                  <input 
                    type="number" 
                    min="1"
                    max="1000"
                    value={customLimits.monthly}
                    onChange={e => setCustomLimits(prev => ({ ...prev, monthly: parseInt(e.target.value) || 1 }))}
                    disabled={customLimits.unlimited}
                  />
                  <span className="input-hint">Max paths per month</span>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox"
                      checked={customLimits.unlimited}
                      onChange={e => setCustomLimits(prev => ({ ...prev, unlimited: e.target.checked }))}
                    />
                    <Crown size={16} />
                    <span>Grant Unlimited Access</span>
                  </label>
                  <span className="input-hint">User can generate unlimited learning paths</span>
                </div>
              </div>

              <div className="current-usage-info">
                <h5>Current Usage</h5>
                <div className="usage-grid">
                  <div className="usage-item">
                    <span className="usage-label">Today:</span>
                    <span className="usage-value">{selectedUserForLimits.usedToday}/{selectedUserForLimits.dailyLimit}</span>
                  </div>
                  <div className="usage-item">
                    <span className="usage-label">This Month:</span>
                    <span className="usage-value">{selectedUserForLimits.usedThisMonth}/{selectedUserForLimits.monthlyLimit}</span>
                  </div>
                  <div className="usage-item">
                    <span className="usage-label">Total:</span>
                    <span className="usage-value">{selectedUserForLimits.totalGenerations}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setEditLimitsModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveLimits}>
                <Save size={16} /> Save Limits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* System Health Tab */}
      {activeTab === 'system-health' && (
        <div className="tab-system-health">
          <div className="sh-header">
            <div>
              <h2><Gauge size={24} /> System Health</h2>
              <p>Live infrastructure monitoring — auto-refreshes every 30 seconds</p>
            </div>
            <div className="sh-header-actions">
              {systemHealthLastRefresh && (
                <span className="sh-last-refresh"><Clock size={14} /> {systemHealthLastRefresh.toLocaleTimeString()}</span>
              )}
              <button className="btn-refresh" onClick={fetchSystemHealth} disabled={systemHealthLoading}>
                <RefreshCw size={16} className={systemHealthLoading ? 'spin' : ''} /> Refresh Now
              </button>
            </div>
          </div>
          {systemHealthLoading && !systemHealth ? (
            <div className="loading-state"><RefreshCw size={32} className="spin" /><p>Loading…</p></div>
          ) : systemHealth ? (
            <>
              <div className={`sh-status-banner ${
                Object.values(systemHealth).some(m=>m?.status==='critical') ? 'critical' :
                Object.values(systemHealth).some(m=>m?.status==='warning') ? 'warning' : 'ok'}`}>
                {Object.values(systemHealth).some(m=>m?.status==='critical') ? (
                  <><AlertCircle size={20}/><strong> Critical Issues</strong> — Immediate attention required</>
                ) : Object.values(systemHealth).some(m=>m?.status==='warning') ? (
                  <><AlertTriangle size={20}/><strong> Warnings Present</strong> — Review metrics below</>
                ) : (
                  <><CheckCircle size={20}/><strong> All Systems Operational</strong></>
                )}
              </div>
              <div className="sh-cards-grid">
                <div className={`sh-metric-card ${systemHealth.apiResponseTime?.status||'ok'}`}>
                  <div className="sh-metric-icon"><Zap size={24}/></div>
                  <div className="sh-metric-body">
                    <div className="sh-metric-label">API Response Time</div>
                    <div className="sh-metric-value">{systemHealth.apiResponseTime?.value??'—'}<span className="sh-metric-unit">{systemHealth.apiResponseTime?.unit||'ms'}</span></div>
                    <div className={`sh-metric-status ${systemHealth.apiResponseTime?.status||'ok'}`}><CheckCircle size={13}/> {(systemHealth.apiResponseTime?.status||'ok').toUpperCase()}</div>
                  </div>
                </div>
                <div className={`sh-metric-card ${systemHealth.dbConnection?.status||'ok'}`}>
                  <div className="sh-metric-icon"><Network size={24}/></div>
                  <div className="sh-metric-body">
                    <div className="sh-metric-label">Database Connection</div>
                    <div className="sh-metric-value">
                      {systemHealth.dbConnection?.status==='ok'?'Connected':'Disconnected'}
                      {systemHealth.dbConnection?.latency!=null&&<span className="sh-metric-unit"> · {systemHealth.dbConnection.latency}ms</span>}
                    </div>
                    <div className={`sh-metric-status ${systemHealth.dbConnection?.status||'ok'}`}>{systemHealth.dbConnection?.status==='ok'?<CheckCircle size={13}/>:<AlertCircle size={13}/>} {(systemHealth.dbConnection?.status||'ok').toUpperCase()}</div>
                  </div>
                </div>
                <div className={`sh-metric-card ${systemHealth.memoryUsage?.status||'ok'}`}>
                  <div className="sh-metric-icon"><Cpu size={24}/></div>
                  <div className="sh-metric-body">
                    <div className="sh-metric-label">Memory Usage</div>
                    <div className="sh-metric-value">{systemHealth.memoryUsage?.percentage??'—'}<span className="sh-metric-unit">%</span></div>
                    {systemHealth.memoryUsage?.used!=null&&(
                      <div className="sh-metric-bar-wrap">
                        <div className="sh-metric-bar"><div className={`sh-metric-bar-fill ${systemHealth.memoryUsage.status||'ok'}`} style={{width:`${Math.min(systemHealth.memoryUsage.percentage,100)}%`}}/></div>
                        <span className="sh-metric-sub">{systemHealth.memoryUsage.used}MB / {systemHealth.memoryUsage.total}MB</span>
                      </div>
                    )}
                    <div className={`sh-metric-status ${systemHealth.memoryUsage?.status||'ok'}`}><CheckCircle size={13}/> {(systemHealth.memoryUsage?.status||'ok').toUpperCase()}</div>
                  </div>
                </div>
                <div className={`sh-metric-card ${systemHealth.uptime?.status||'ok'}`}>
                  <div className="sh-metric-icon"><Power size={24}/></div>
                  <div className="sh-metric-body">
                    <div className="sh-metric-label">Server Uptime</div>
                    <div className="sh-metric-value" style={{fontSize:'1.25rem'}}>{systemHealth.uptime?.formatted||'—'}</div>
                    <div className="sh-metric-status ok"><CheckCircle size={13}/> RUNNING</div>
                  </div>
                </div>
                {systemHealth.errorRate&&(
                  <div className={`sh-metric-card ${systemHealth.errorRate.status||'ok'}`}>
                    <div className="sh-metric-icon"><AlertTriangle size={24}/></div>
                    <div className="sh-metric-body">
                      <div className="sh-metric-label">Error Rate</div>
                      <div className="sh-metric-value">{systemHealth.errorRate.value}<span className="sh-metric-unit">%</span></div>
                      <div className={`sh-metric-status ${systemHealth.errorRate.status||'ok'}`}>{systemHealth.errorRate.status==='ok'?<CheckCircle size={13}/>:<AlertTriangle size={13}/>} {(systemHealth.errorRate.status||'ok').toUpperCase()}</div>
                    </div>
                  </div>
                )}
                {systemHealth.activeConnections&&(
                  <div className={`sh-metric-card ${systemHealth.activeConnections.status||'ok'}`}>
                    <div className="sh-metric-icon"><Radio size={24}/></div>
                    <div className="sh-metric-body">
                      <div className="sh-metric-label">Active Connections</div>
                      <div className="sh-metric-value">{systemHealth.activeConnections.value}</div>
                      <div className="sh-metric-status ok"><CheckCircle size={13}/> LIVE</div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="sh-empty"><Gauge size={40}/><p>No health data. Click Refresh Now.</p></div>
          )}
        </div>
      )}

      </AdminDashboardShell>

      <AdminCopilotDock
        token={token}
        open={copilotOpen}
        onToggle={() => setCopilotOpen((o) => !o)}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
        confirmText={confirmModal.confirmText || 'Delete'}
        danger={confirmModal.danger}
      />
    </div>
  );
};

export default AdminDashboard;
